import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from 'react';
import type {
  ChangeEvent,
  DragEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent
} from 'react';
import blockTypeSchema from './blockTypes.json';
import type { FeatureContext } from '../../types';
import './workflow-builder.css';

interface BlockTypeDefaults {
  name: string;
  code?: string;
  inputs?: string[];
  outputs?: string[];
}

interface BlockTypeCapabilities {
  code?: boolean;
  editableInputs?: boolean;
  editableOutputs?: boolean;
  notes?: string;
}

type BlockType = 'start' | 'code' | 'transform' | 'if' | 'llm' | 'end';

interface BlockTypeDefinition {
  type: BlockType;
  title: string;
  description: string;
  defaults: BlockTypeDefaults;
  capabilities?: BlockTypeCapabilities;
}

interface BlockTypeSchema {
  version: string;
  types: BlockTypeDefinition[];
}

interface Position {
  x: number;
  y: number;
}

interface WorkflowBlock {
  id: string;
  type: BlockType;
  name: string;
  code?: string;
  position: Position;
  inputs: string[];
  outputs: string[];
  transformMappings?: TransformMapping[];
  conditions?: ConditionRule[];
  llm?: LlmConfiguration;
}

interface WorkflowConnection {
  from: string;
  to: string;
  fromOutput?: string;
}

interface TransformMapping {
  source: string;
  target: string;
}

type ConditionOperator = 'equals' | 'contains' | 'isEmpty' | 'always';

interface ConditionRule {
  id: string;
  operator: ConditionOperator;
  left: string;
  right?: string;
  output: string;
}

interface LlmPromptSource {
  type: 'input' | 'global';
  value: string;
}

interface LlmConfiguration {
  endpoint: string;
  deploymentName: string;
  apiKey: string;
  promptSource: LlmPromptSource;
}

interface GlobalVariable {
  name: string;
  value: string;
  description?: string;
}

type GlobalVariableEntry = GlobalVariable & { id: string };

interface WorkflowDocument {
  version: string;
  name: string;
  blocks: WorkflowBlock[];
  connections: WorkflowConnection[];
  globals: GlobalVariable[];
}

interface ExportPreview {
  format: 'json' | 'yaml';
  content: string;
}

interface SavedWorkflowSummary {
  name: string;
  updatedAt: string;
}

interface SavedWorkflowDetail extends SavedWorkflowSummary {
  document: WorkflowDocument;
  exportJson: string;
  exportYaml: string;
}

const KNOWN_BLOCK_TYPES: BlockType[] = ['start', 'code', 'transform', 'if', 'llm', 'end'];

const schema = blockTypeSchema as BlockTypeSchema;
const BLOCK_TYPE_DEFINITIONS = new Map<BlockType, BlockTypeDefinition>();

for (const definition of schema.types ?? []) {
  if (!definition || !KNOWN_BLOCK_TYPES.includes(definition.type)) {
    continue;
  }

  BLOCK_TYPE_DEFINITIONS.set(definition.type, {
    ...definition,
    defaults: {
      ...definition.defaults,
      inputs: definition.defaults.inputs ?? [],
      outputs: definition.defaults.outputs ?? []
    },
    capabilities: {
      editableInputs: true,
      editableOutputs: true,
      code: definition.defaults.code !== undefined,
      ...definition.capabilities
    }
  });
}

const BLOCK_TYPES: BlockType[] = KNOWN_BLOCK_TYPES.filter((type) => BLOCK_TYPE_DEFINITIONS.has(type));

const BLOCK_TYPE_TITLES: Record<BlockType, string> = {
  start: 'Start',
  code: 'Code',
  transform: 'Transform',
  if: 'If',
  llm: 'LLM',
  end: 'End'
};

for (const [type, definition] of BLOCK_TYPE_DEFINITIONS.entries()) {
  BLOCK_TYPE_TITLES[type] = definition.title;
}

const DEFAULT_POSITIONS: Partial<Record<BlockType, Position>> = {
  start: { x: -380, y: 0 },
  transform: { x: 0, y: 0 },
  if: { x: 0, y: 180 },
  llm: { x: 200, y: 40 },
  end: { x: 380, y: 0 }
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 140;
const CANVAS_WIDTH = 6000;
const CANVAS_HEIGHT = 4000;
const CANVAS_CENTER_X = CANVAS_WIDTH / 2;
const CANVAS_CENTER_Y = CANVAS_HEIGHT / 2;
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 2.4;
const CONNECTOR_OFFSET_X = 22;
const CONNECTOR_RADIUS = 10;
const CONNECTOR_GAP = 6;
const CONNECTOR_LINE_PADDING = 8;
const CONNECTOR_VISUAL_PADDING = CONNECTOR_OFFSET_X + CONNECTOR_GAP + CONNECTOR_LINE_PADDING + CONNECTOR_RADIUS * 2;
const VIEWPORT_MARGIN = 80;
const GRID_SIZE = 120;

function createBlockId(type: BlockType): string {
  const random = Math.random().toString(36).slice(2, 10);
  const timestamp = Date.now().toString(36);
  return `${type}-${timestamp}-${random}`;
}

function createGlobalVariableId(): string {
  const random = Math.random().toString(36).slice(2, 8);
  const timestamp = Date.now().toString(36);
  return `global-${timestamp}-${random}`;
}

function buildDefaultTransformMappings(outputs: string[]): TransformMapping[] {
  return outputs.map((target) => ({
    source: target,
    target
  }));
}

function getTransformMappings(block: WorkflowBlock): TransformMapping[] {
  const mappings = block.transformMappings ?? buildDefaultTransformMappings(block.outputs);
  return mappings.map((mapping) => ({
    source: (mapping.source ?? '').trim(),
    target: (mapping.target ?? '').trim()
  }));
}

function createConditionRuleId(): string {
  const random = Math.random().toString(36).slice(2, 8);
  return `cond-${Date.now().toString(36)}-${random}`;
}

function buildDefaultConditionRules(outputs: string[]): ConditionRule[] {
  if (!outputs || outputs.length === 0) {
    return [
      {
        id: createConditionRuleId(),
        operator: 'equals',
        left: '',
        right: '',
        output: 'ifTrue'
      },
      {
        id: createConditionRuleId(),
        operator: 'always',
        left: '',
        right: '',
        output: 'else'
      }
    ];
  }
  return outputs.map((output, index) => ({
    id: createConditionRuleId(),
    operator: index === outputs.length - 1 ? 'always' : 'equals',
    left: '',
    right: '',
    output: output.trim().length > 0 ? output.trim() : `branch${index + 1}`
  }));
}

function getConditionRules(block: WorkflowBlock): ConditionRule[] {
  const rules = block.conditions ?? buildDefaultConditionRules(block.outputs);
  return rules.map((rule, index) => ({
    id: rule.id || createConditionRuleId(),
    operator: rule.operator ?? (index === rules.length - 1 ? 'always' : 'equals'),
    left: (rule.left ?? '').trim(),
    right: rule.operator === 'isEmpty' || rule.operator === 'always' ? '' : (rule.right ?? '').toString(),
    output: (rule.output ?? block.outputs[index] ?? `branch${index + 1}`).trim() || `branch${index + 1}`
  }));
}

function createDefaultLlmConfig(block?: Pick<WorkflowBlock, 'inputs'>): LlmConfiguration {
  const primaryInput = block?.inputs?.[0] ?? '';
  return {
    endpoint: '',
    deploymentName: '',
    apiKey: '',
    promptSource: {
      type: primaryInput ? 'input' : 'global',
      value: primaryInput
    }
  };
}

function sanitizeLlmPromptSource(value: unknown, block: WorkflowBlock): LlmPromptSource {
  const fallback = createDefaultLlmConfig(block).promptSource;
  if (!value || typeof value !== 'object') {
    return fallback;
  }
  const candidate = value as { type?: string; value?: unknown };
  const nextType: LlmPromptSource['type'] = candidate.type === 'global' ? 'global' : 'input';
  const nextValue = typeof candidate.value === 'string' ? candidate.value.trim() : fallback.value;
  return {
    type: nextType,
    value: nextValue
  };
}

function sanitizeLlmConfig(raw: unknown, block: WorkflowBlock): LlmConfiguration {
  const fallback = createDefaultLlmConfig(block);
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }
  const candidate = raw as {
    endpoint?: unknown;
    deploymentName?: unknown;
    deployment_name?: unknown;
    apiKey?: unknown;
    api_key?: unknown;
    promptSource?: unknown;
    prompt_source?: unknown;
  };
  return {
    endpoint: typeof candidate.endpoint === 'string' ? candidate.endpoint : fallback.endpoint,
    deploymentName:
      typeof candidate.deploymentName === 'string'
        ? candidate.deploymentName
        : typeof candidate.deployment_name === 'string'
          ? candidate.deployment_name
          : fallback.deploymentName,
    apiKey:
      typeof candidate.apiKey === 'string'
        ? candidate.apiKey
        : typeof candidate.api_key === 'string'
          ? candidate.api_key
          : fallback.apiKey,
    promptSource: sanitizeLlmPromptSource(candidate.promptSource ?? candidate.prompt_source, block)
  };
}

function getLlmConfig(block: WorkflowBlock): LlmConfiguration {
  if (block.llm) {
    return sanitizeLlmConfig(block.llm, block);
  }
  return createDefaultLlmConfig(block);
}

function getHandleRatio(total: number, index: number): number {
  if (total <= 0) {
    return 0.5;
  }
  const clamped = Math.max(0, Math.min(total - 1, index));
  return (clamped + 1) / (total + 1);
}

function getOutputHandleRatio(block: WorkflowBlock, outputName?: string): number {
  const total = block.outputs.length;
  if (total === 0) {
    return 0.5;
  }
  const index = outputName ? block.outputs.indexOf(outputName) : -1;
  const fallbackIndex = index >= 0 ? index : Math.floor(total / 2);
  return getHandleRatio(total, fallbackIndex);
}

function getOutputHandleRatioByIndex(block: WorkflowBlock, index: number): number {
  const total = block.outputs.length || 1;
  return getHandleRatio(total, index);
}

function coerceToString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }

  return String(value);
}

const WORKFLOW_API_PREFIX = '/productivity/workflow-builder';
const WORKFLOW_EXECUTION_API_PREFIX = '/workflowbuilder';

function normalizeApiBaseUrl(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  if (value === '/') {
    return '';
  }
  return value.replace(/\/+$/, '');
}

async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const data = await response.clone().json();
    if (data && typeof data === 'object') {
      if (typeof data.detail === 'string' && data.detail.trim().length > 0) {
        return data.detail;
      }
      if (typeof data.message === 'string' && data.message.trim().length > 0) {
        return data.message;
      }
    }
  } catch {
    // Ignore JSON parsing errors; fall back to text.
  }

  try {
    const text = await response.text();
    if (text.trim().length > 0) {
      return text.trim();
    }
  } catch {
    // Ignore text parsing errors.
  }

  return `Request failed with status ${response.status}`;
}

function createInitialWorkflow(): WorkflowDocument {
  const startId = createBlockId('start');
  const endId = createBlockId('end');
  const startDefinition = getBlockDefinition('start');
  const endDefinition = getBlockDefinition('end');
  return {
    version: '1.0',
    name: 'Untitled Workflow',
    blocks: [
      {
        id: startId,
        type: 'start',
        name: startDefinition.defaults.name ?? 'Start',
        position: DEFAULT_POSITIONS.start ?? { x: -200, y: 0 },
        inputs: [...(startDefinition.defaults.inputs ?? [])],
        outputs: [...(startDefinition.defaults.outputs ?? [])]
      },
      {
        id: endId,
        type: 'end',
        name: endDefinition.defaults.name ?? 'End',
        position: DEFAULT_POSITIONS.end ?? { x: 200, y: 0 },
        inputs: [...(endDefinition.defaults.inputs ?? [])],
        outputs: [...(endDefinition.defaults.outputs ?? [])]
      }
    ],
    connections: [],
    globals: []
  };
}

function isBlockType(value: unknown): value is BlockType {
  return KNOWN_BLOCK_TYPES.includes(value as BlockType);
}

function parsePosition(value: unknown): Position | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as { x?: unknown; y?: unknown };
  const x = typeof candidate.x === 'number' ? candidate.x : Number(candidate.x);
  const y = typeof candidate.y === 'number' ? candidate.y : Number(candidate.y);

  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return { x, y };
}

function getBlockDefinition(type: BlockType): BlockTypeDefinition {
  const definition = BLOCK_TYPE_DEFINITIONS.get(type);
  if (definition) {
    return definition;
  }

  return {
    type,
    title: BLOCK_TYPE_TITLES[type] ?? type,
    description: 'Custom workflow block.',
    defaults: {
      name: BLOCK_TYPE_TITLES[type] ?? type,
      inputs: [],
      outputs: []
    },
    capabilities: {
      editableInputs: true,
      editableOutputs: true,
      code: type === 'code' || type === 'transform'
    }
  };
}

function sanitizePortList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) {
    return [...fallback];
  }

  const sanitized: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') {
      continue;
    }
    const trimmed = entry.trim();
    if (trimmed.length === 0 || sanitized.includes(trimmed)) {
      continue;
    }
    sanitized.push(trimmed);
  }

  return sanitized;
}

function formatNewBlockName(type: BlockType, instance: number): string {
  const definition = getBlockDefinition(type);
  const baseName = definition.defaults.name ?? BLOCK_TYPE_TITLES[type] ?? type;

  if (type === 'start') {
    return baseName;
  }

  if (instance <= 1) {
    return baseName;
  }

  return `${baseName} ${instance}`;
}

function formatCoordinate(value: number): string {
  if (!Number.isFinite(value)) {
    return '0';
  }

  if (Number.isInteger(value)) {
    return value.toString();
  }

  return Number(value.toFixed(2)).toString();
}

function appendYamlArray(lines: string[], indent: string, key: string, values: string[]): void {
  if (!values || values.length === 0) {
    lines.push(`${indent}${key}: []`);
    return;
  }

  lines.push(`${indent}${key}:`);
  for (const item of values) {
    lines.push(`${indent}  - ${JSON.stringify(item)}`);
  }
}

function autoPosition(index: number): Position {
  const columns = 4;
  const columnWidth = 320;
  const rowHeight = 200;
  const column = index % columns;
  const row = Math.floor(index / columns);
  const columnOffset = column - (columns - 1) / 2;
  return {
    x: columnOffset * columnWidth,
    y: row * rowHeight + 120
  };
}

function normalizeWorkflow(document: WorkflowDocument): WorkflowDocument {
  const seenSingletons = new Set<BlockType>();
  const blocks: WorkflowBlock[] = [];
  let autoIndex = 0;
  const globals: GlobalVariable[] = [];
  const globalNames = new Set<string>();

  const addGlobalVariable = (nameCandidate: unknown, valueCandidate: unknown, descriptionCandidate?: unknown) => {
    const baseName =
      typeof nameCandidate === 'string' && nameCandidate.trim().length > 0
        ? nameCandidate.trim()
        : `global${globals.length + 1}`;
    let name = baseName;
    let suffix = 2;
    while (globalNames.has(name)) {
      name = `${baseName}_${suffix++}`;
    }

    const variable: GlobalVariable = {
      name,
      value: coerceToString(valueCandidate)
    };

    if (typeof descriptionCandidate === 'string' && descriptionCandidate.trim().length > 0) {
      variable.description = descriptionCandidate.trim();
    }

    globalNames.add(name);
    globals.push(variable);
  };

  const collectGlobals = (source: unknown) => {
    if (!source) {
      return;
    }

    if (Array.isArray(source)) {
      for (const entry of source) {
        if (!entry) {
          continue;
        }
        if (typeof entry === 'string') {
          addGlobalVariable(entry, '');
        } else if (typeof entry === 'object') {
          const record = entry as Partial<GlobalVariable> & Record<string, unknown>;
          const valueCandidate = 'value' in record ? record.value : '';
          const descriptionCandidate = 'description' in record ? record.description : undefined;
          addGlobalVariable(record.name, valueCandidate, descriptionCandidate);
        }
      }
      return;
    }

    if (typeof source === 'object') {
      for (const [name, valueCandidate] of Object.entries(source as Record<string, unknown>)) {
        if (valueCandidate && typeof valueCandidate === 'object' && !Array.isArray(valueCandidate)) {
          const record = valueCandidate as Record<string, unknown>;
          const value = 'value' in record ? record.value : valueCandidate;
          const description = typeof record.description === 'string' ? record.description : undefined;
          addGlobalVariable(name, value, description);
        } else {
          addGlobalVariable(name, valueCandidate, undefined);
        }
      }
    }
  };

  collectGlobals((document as { globals?: unknown }).globals ?? []);
  collectGlobals((document as { globalVariables?: unknown }).globalVariables);

  for (const candidate of document.blocks) {
    if (!candidate || typeof candidate !== 'object' || !isBlockType((candidate as WorkflowBlock).type)) {
      continue;
    }

    const type = candidate.type;
    if (type === 'start' && seenSingletons.has(type)) {
      continue;
    }

    if (type === 'start') {
      seenSingletons.add(type);
    }

    const definition = getBlockDefinition(type);
    const name =
      typeof candidate.name === 'string' && candidate.name.trim().length > 0
        ? candidate.name.trim()
        : definition.defaults.name ?? BLOCK_TYPE_TITLES[type];

    const existingPosition = parsePosition((candidate as WorkflowBlock).position);
    const position = existingPosition ?? DEFAULT_POSITIONS[type] ?? autoPosition(autoIndex++);

    const inputs = sanitizePortList((candidate as WorkflowBlock).inputs, definition.defaults.inputs ?? []);
    let outputs = sanitizePortList((candidate as WorkflowBlock).outputs, definition.defaults.outputs ?? []);

    const normalizedBlock: WorkflowBlock = {
      id:
        typeof candidate.id === 'string' && candidate.id.trim().length > 0
          ? candidate.id
          : createBlockId(type),
      type,
      name,
      position,
      inputs,
      outputs
    };

    if (definition.capabilities?.code) {
      normalizedBlock.code =
        typeof (candidate as WorkflowBlock).code === 'string'
          ? (candidate as WorkflowBlock).code
          : definition.defaults.code ?? '';
    }

    if (type === 'transform') {
      const mappingCandidates: TransformMapping[] = [];
      const seenTargets = new Set<string>();

      const addMapping = (sourceCandidate: unknown, targetCandidate: unknown) => {
        const target = typeof targetCandidate === 'string' ? targetCandidate.trim() : '';
        if (!target || seenTargets.has(target)) {
          return;
        }
        const source = typeof sourceCandidate === 'string' ? sourceCandidate.trim() : '';
        seenTargets.add(target);
        mappingCandidates.push({
          source: source || target,
          target
        });
      };

      const collectMappings = (raw: unknown) => {
        if (!raw) {
          return;
        }
        if (Array.isArray(raw)) {
          for (const entry of raw) {
            if (!entry) {
              continue;
            }
            if (typeof entry === 'string') {
              addMapping(entry, entry);
            } else if (typeof entry === 'object') {
              const record = entry as Record<string, unknown>;
              const sourceCandidate = record.source ?? record.path ?? record.from ?? record.input ?? record.name;
              const targetCandidate = record.target ?? record.output ?? record.name ?? record.as;
              addMapping(sourceCandidate, targetCandidate);
            }
          }
        } else if (typeof raw === 'object') {
          for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
            addMapping(value, key);
          }
        }
      };

      collectMappings((candidate as WorkflowBlock).transformMappings);
      collectMappings((candidate as { transform?: { mappings?: unknown } }).transform?.mappings);

      if (mappingCandidates.length === 0) {
        outputs = sanitizePortList(outputs, definition.defaults.outputs ?? []);
        if (outputs.length === 0) {
          outputs = ['output1'];
        }
        for (const target of outputs) {
          const cleanTarget = target.trim();
          if (!cleanTarget || seenTargets.has(cleanTarget)) {
            continue;
          }
          seenTargets.add(cleanTarget);
          mappingCandidates.push({
            source: cleanTarget,
            target: cleanTarget
          });
        }
      }

      normalizedBlock.transformMappings = mappingCandidates.map((mapping) => ({
        source: mapping.source.trim(),
        target: mapping.target.trim()
      }));
      normalizedBlock.outputs = normalizedBlock.transformMappings.map((mapping) => mapping.target);
    }

    if (normalizedBlock.type === 'if') {
      const incomingConditions =
        (candidate as WorkflowBlock).conditions ??
        ((candidate as { branches?: ConditionRule[] }).branches ?? []);
      const tempBlock: WorkflowBlock = {
        ...normalizedBlock,
        conditions: Array.isArray(incomingConditions) ? incomingConditions : undefined
      };
      const rules = getConditionRules(tempBlock);
      normalizedBlock.conditions = rules;
      normalizedBlock.outputs = rules.map((rule) => rule.output);
    }

    if (normalizedBlock.type === 'llm') {
      normalizedBlock.llm = sanitizeLlmConfig(
        (candidate as WorkflowBlock).llm ?? (candidate as { llmConfig?: unknown }).llmConfig,
        normalizedBlock
      );
    }

    blocks.push(normalizedBlock);
  }

  const ensureBlock = (type: BlockType) => {
    if (blocks.some((block) => block.type === type)) {
      return;
    }

    const definition = getBlockDefinition(type);
    const fallbackPosition = DEFAULT_POSITIONS[type] ?? autoPosition(autoIndex++);
    const newBlock: WorkflowBlock = {
      id: createBlockId(type),
      type,
      name: definition.defaults.name ?? BLOCK_TYPE_TITLES[type],
      position: fallbackPosition,
      inputs: [...(definition.defaults.inputs ?? [])],
      outputs: [...(definition.defaults.outputs ?? [])]
    };

    if (definition.capabilities?.code) {
      newBlock.code = definition.defaults.code ?? '';
    }

    if (type === 'transform') {
      newBlock.transformMappings = buildDefaultTransformMappings(newBlock.outputs);
      newBlock.outputs = newBlock.transformMappings.map((mapping) => mapping.target);
    }

    if (type === 'if') {
      newBlock.conditions = buildDefaultConditionRules(newBlock.outputs);
      newBlock.outputs = newBlock.conditions.map((rule) => rule.output);
    }

    if (type === 'llm') {
      newBlock.llm = createDefaultLlmConfig(newBlock);
    }

    if (type === 'start') {
      blocks.unshift(newBlock);
    } else if (type === 'end') {
      const endIndex = blocks.findIndex((block) => block.type === 'end');
      if (endIndex >= 0) {
        blocks.splice(endIndex + 1, 0, newBlock);
      } else {
        blocks.push(newBlock);
      }
    } else {
      blocks.push(newBlock);
    }
  };

  ensureBlock('start');
  if (!blocks.some((block) => block.type === 'end')) {
    ensureBlock('end');
  }

  const validIds = new Set(blocks.map((block) => block.id));

  const connections: WorkflowConnection[] = [];
  if (Array.isArray(document.connections)) {
    for (const candidate of document.connections) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const fromRaw = (candidate as WorkflowConnection).from;
      const toRaw = (candidate as WorkflowConnection).to;
      const fromOutputRaw =
        (candidate as WorkflowConnection).fromOutput ??
        (candidate as { from_output?: string }).from_output;
      if (typeof fromRaw !== 'string' || typeof toRaw !== 'string') {
        continue;
      }

      const from = fromRaw.trim();
      const to = toRaw.trim();
      if (!from || !to || from === to) {
        continue;
      }

      if (!validIds.has(from) || !validIds.has(to)) {
        continue;
      }
      const fromOutput = typeof fromOutputRaw === 'string' ? fromOutputRaw.trim() : '';

      if (
        connections.some(
          (connection) =>
            connection.from === from &&
            connection.to === to &&
            (connection.fromOutput ?? '') === (fromOutput || '')
        )
      ) {
        continue;
      }

      connections.push({
        from,
        to,
        ...(fromOutput ? { fromOutput } : {})
      });
    }
  }

  return {
    version: typeof document.version === 'string' && document.version.trim().length > 0 ? document.version : '1.0',
    name:
      typeof document.name === 'string' && document.name.trim().length > 0
        ? document.name.trim()
        : 'Untitled Workflow',
    blocks,
    connections,
    globals
  };
}

function exportToJson(document: WorkflowDocument): string {
  return JSON.stringify(document, null, 2);
}

function exportToYaml(document: WorkflowDocument): string {
  const lines: string[] = [];
  lines.push(`version: ${JSON.stringify(document.version)}`);
  lines.push(`name: ${JSON.stringify(document.name)}`);
  lines.push('globals:');
  if (document.globals.length === 0) {
    lines.push('  []');
  } else {
    for (const variable of document.globals) {
      lines.push('  - name: ' + JSON.stringify(variable.name));
      lines.push('    value: ' + JSON.stringify(variable.value));
      if (variable.description && variable.description.trim().length > 0) {
        lines.push('    description: ' + JSON.stringify(variable.description));
      }
    }
  }
  lines.push('blocks:');

  if (document.blocks.length === 0) {
    lines.push('  []');
  } else {
    for (const block of document.blocks) {
      const definition = getBlockDefinition(block.type);
      lines.push('  - id: ' + JSON.stringify(block.id));
      lines.push('    type: ' + JSON.stringify(block.type));
      lines.push('    name: ' + JSON.stringify(block.name));
      lines.push('    position:');
      lines.push('      x: ' + formatCoordinate(block.position.x));
      lines.push('      y: ' + formatCoordinate(block.position.y));
      appendYamlArray(lines, '    ', 'inputs', block.inputs);
      appendYamlArray(lines, '    ', 'outputs', block.outputs);

      if (definition.capabilities?.code) {
        const codeValue = block.code ?? '';
        if (codeValue.includes('\n')) {
          lines.push('    code: |');
          for (const codeLine of codeValue.split(/\r?\n/)) {
            lines.push('      ' + codeLine);
          }
        } else {
          lines.push('    code: ' + JSON.stringify(codeValue));
        }
      }

      if (block.transformMappings && block.transformMappings.length > 0) {
        lines.push('    transformMappings:');
        for (const mapping of block.transformMappings) {
          lines.push('      - source: ' + JSON.stringify(mapping.source ?? ''));
          lines.push('        target: ' + JSON.stringify(mapping.target ?? ''));
        }
      }

      if (block.llm) {
        lines.push('    llm:');
        lines.push('      endpoint: ' + JSON.stringify(block.llm.endpoint ?? ''));
        lines.push('      deploymentName: ' + JSON.stringify(block.llm.deploymentName ?? ''));
        lines.push('      apiKey: ' + JSON.stringify(block.llm.apiKey ?? ''));
        if (block.llm.promptSource) {
          lines.push('      promptSource:');
          lines.push('        type: ' + JSON.stringify(block.llm.promptSource.type));
          lines.push('        value: ' + JSON.stringify(block.llm.promptSource.value ?? ''));
        }
      }
    }
  }

  lines.push('connections:');
  if (document.connections.length === 0) {
    lines.push('  []');
  } else {
    for (const connection of document.connections) {
      lines.push('  - from: ' + JSON.stringify(connection.from));
      lines.push('    to: ' + JSON.stringify(connection.to));
      if (connection.fromOutput) {
        lines.push('    fromOutput: ' + JSON.stringify(connection.fromOutput));
      }
    }
  }

  return lines.join('\n');
}

function parseWorkflowJson(text: string): WorkflowDocument | null {
  try {
    const value = JSON.parse(text);
    if (value && typeof value === 'object') {
      return normalizeWorkflow(value as WorkflowDocument);
    }
  } catch (error) {
    console.error('Unable to parse JSON workflow', error);
  }
  return null;
}

function parseWorkflowYaml(text: string): WorkflowDocument | null {
  const lines = text.replace(/\r/g, '').split('\n');
  const rawGlobals: Partial<GlobalVariable>[] = [];
  const rawBlocks: Partial<WorkflowBlock>[] = [];
  const rawConnections: Partial<WorkflowConnection>[] = [];
  let version: string | undefined;
  let name: string | undefined;
  let section: 'root' | 'globals' | 'blocks' | 'connections' = 'root';
  let currentGlobal: Partial<GlobalVariable> | null = null;
  let currentBlock: Partial<WorkflowBlock> | null = null;
  let currentMapping: TransformMapping | null = null;
  let currentLlmConfig: Partial<LlmConfiguration> | null = null;
  let readingLlm = false;
  let readingLlmPrompt = false;
  let currentConnection: Partial<WorkflowConnection> | null = null;
  let arrayTarget: 'inputs' | 'outputs' | 'transformMappings' | null = null;
  let readingPosition = false;
  let multilineKey: 'code' | null = null;
  let multilineIndent = 0;
  const multilineBuffer: string[] = [];

  const commitMultiline = () => {
    if (multilineKey && currentBlock) {
      currentBlock[multilineKey] = multilineBuffer.join('\n');
    }
    multilineKey = null;
    multilineBuffer.length = 0;
  };

  const commitGlobal = () => {
    if (currentGlobal) {
      rawGlobals.push(currentGlobal);
    }
    currentGlobal = null;
  };

  const commitBlock = () => {
    if (currentBlock) {
      rawBlocks.push(currentBlock);
    }
    currentBlock = null;
    arrayTarget = null;
    readingPosition = false;
    currentMapping = null;
    currentLlmConfig = null;
    readingLlm = false;
    readingLlmPrompt = false;
  };

  const commitConnection = () => {
    if (currentConnection) {
      rawConnections.push(currentConnection);
    }
    currentConnection = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/#.*$/, '');
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }

    const indent = line.match(/^\s*/)?.[0].length ?? 0;

    if (multilineKey && indent <= multilineIndent) {
      commitMultiline();
    }

    if (multilineKey) {
      const baseIndent = multilineIndent + 2;
      const sliceIndex = Math.min(line.length, baseIndent);
      multilineBuffer.push(line.slice(sliceIndex));
      continue;
    }

    if (indent <= 2) {
      arrayTarget = null;
    }

    if (section === 'root') {
      if (trimmed.startsWith('version:')) {
        version = String(parseYamlScalar(trimmed.slice(8).trim()));
        continue;
      }
      if (trimmed.startsWith('name:')) {
        const parsedName = parseYamlScalar(trimmed.slice(5).trim());
        name = typeof parsedName === 'string' ? parsedName : undefined;
        continue;
      }
      if (trimmed.startsWith('globals:')) {
        commitGlobal();
        section = 'globals';
        continue;
      }
      if (trimmed.startsWith('blocks:')) {
        commitGlobal();
        section = 'blocks';
        continue;
      }
      if (trimmed.startsWith('connections:')) {
        commitGlobal();
        section = 'connections';
        continue;
      }
      continue;
    }

    if (section === 'globals') {
      if (indent === 0 && trimmed.startsWith('blocks:')) {
        commitGlobal();
        section = 'blocks';
        continue;
      }
      if (indent === 0 && trimmed.startsWith('connections:')) {
        commitGlobal();
        section = 'connections';
        continue;
      }

      if (trimmed === '[]') {
        continue;
      }

      if (indent === 2 && trimmed.startsWith('- ')) {
        commitGlobal();
        currentGlobal = {};
        const remainder = trimmed.slice(2).trim();
        if (remainder.length > 0) {
          const [key, valuePart] = remainder.split(/:\s*/, 2);
          if (key && valuePart !== undefined) {
            assignGlobalProperty(currentGlobal, key, valuePart);
          }
        }
        continue;
      }

      if (!currentGlobal) {
        continue;
      }

      if (indent === 4 && trimmed.includes(':')) {
        const [key, valuePart = ''] = trimmed.split(/:\s*/, 2);
        assignGlobalProperty(currentGlobal, key, valuePart);
        continue;
      }

      if (indent <= 2) {
        commitGlobal();
        section = 'root';
      }

      continue;
    }

    if (section === 'blocks') {
      if (trimmed === '[]') {
        continue;
      }

      if (indent === 2 && trimmed.startsWith('- ')) {
        commitBlock();
        currentBlock = {};
        const remainder = trimmed.slice(2).trim();
        if (remainder.length > 0) {
          const [key, valuePart] = remainder.split(/:\s*/, 2);
          if (key && valuePart !== undefined) {
            assignBlockProperty(currentBlock, key, valuePart);
          }
        }
        continue;
      }

      if (!currentBlock) {
        continue;
      }

      if (readingLlm && indent <= 4) {
        readingLlm = false;
        readingLlmPrompt = false;
        currentLlmConfig = null;
      }

      if (arrayTarget === 'transformMappings') {
        if (indent === 6 && trimmed.startsWith('- ')) {
          currentMapping = { source: '', target: '' };
          (currentBlock.transformMappings = currentBlock.transformMappings ?? []).push(currentMapping);
          const remainder = trimmed.slice(2).trim();
          if (remainder.length > 0) {
            const [key, valuePart = ''] = remainder.split(/:\s*/, 2);
            assignTransformMappingProperty(currentMapping, key, valuePart);
          }
          continue;
        }

        if (indent === 8 && trimmed.includes(':') && currentMapping) {
          const [key, valuePart = ''] = trimmed.split(/:\s*/, 2);
          assignTransformMappingProperty(currentMapping, key, valuePart);
          continue;
        }

        if (indent <= 4) {
          currentMapping = null;
          arrayTarget = null;
        }
      }

        if (readingLlm && indent === 6 && trimmed.includes(':')) {
          const [llmKey, llmValue = ''] = trimmed.split(/:\s*/, 2);
          if (llmKey === 'promptSource') {
            readingLlmPrompt = true;
            if (!currentLlmConfig?.promptSource) {
              currentLlmConfig = currentLlmConfig ?? {};
              currentLlmConfig.promptSource = { type: 'input', value: '' } as LlmConfiguration['promptSource'];
            }
            continue;
          }
          const parsed = parseYamlScalar(llmValue);
          if (!currentLlmConfig) {
            currentLlmConfig = {};
          }
          if (llmKey === 'endpoint' && typeof parsed === 'string') {
            currentLlmConfig.endpoint = parsed;
          } else if (llmKey === 'deploymentName' && typeof parsed === 'string') {
            currentLlmConfig.deploymentName = parsed;
          } else if (llmKey === 'apiKey' && typeof parsed === 'string') {
            currentLlmConfig.apiKey = parsed;
          }
          continue;
        }

        if (readingLlmPrompt && indent === 8 && trimmed.includes(':')) {
          const [promptKey, promptValue = ''] = trimmed.split(/:\s*/, 2);
          if (!currentLlmConfig) {
            currentLlmConfig = {};
          }
          const parsed = parseYamlScalar(promptValue);
          if (!currentLlmConfig.promptSource) {
            currentLlmConfig.promptSource = { type: 'input', value: '' } as LlmConfiguration['promptSource'];
          }
          if (promptKey === 'type' && typeof parsed === 'string') {
            currentLlmConfig.promptSource.type = parsed === 'global' ? 'global' : 'input';
          } else if (promptKey === 'value' && typeof parsed === 'string') {
            currentLlmConfig.promptSource.value = parsed;
          }
          continue;
        }

        if (indent === 4 && trimmed.includes(':')) {
          const [key, valuePart = ''] = trimmed.split(/:\s*/, 2);
          if (key === 'position') {
            readingPosition = true;
            currentBlock.position = { x: 0, y: 0 };
            continue;
        }

        if (key === 'inputs' || key === 'outputs') {
          const target = key as 'inputs' | 'outputs';
          if (valuePart === '[]') {
            currentBlock[target] = [];
            arrayTarget = null;
          } else if (valuePart.length > 0) {
            const parsed = parseYamlScalar(valuePart);
            if (typeof parsed === 'string') {
              currentBlock[target] = [parsed];
            } else if (Array.isArray(parsed)) {
              currentBlock[target] = parsed.filter((item): item is string => typeof item === 'string');
            } else {
              currentBlock[target] = [];
            }
            arrayTarget = null;
          } else {
            (currentBlock[target] = currentBlock[target] ?? []);
            arrayTarget = target;
          }
          continue;
        }

          if (key === 'transformMappings') {
            currentBlock.transformMappings = [];
            currentMapping = null;
            if (valuePart === '[]') {
              arrayTarget = null;
          } else if (valuePart.length > 0) {
            const parsed = parseYamlScalar(valuePart);
            if (Array.isArray(parsed)) {
              currentBlock.transformMappings = parsed
                .map((entry) => (typeof entry === 'object' && entry ? entry : null))
                .filter((entry): entry is Record<string, unknown> => entry !== null)
                .map((entry) => ({
                  source: typeof entry.source === 'string' ? entry.source : '',
                  target: typeof entry.target === 'string' ? entry.target : ''
                }));
            }
            arrayTarget = null;
          } else {
            arrayTarget = 'transformMappings';
          }
          continue;
        }

        if (key === 'code') {
          if (valuePart === '|') {
            multilineKey = 'code';
            multilineIndent = indent;
            multilineBuffer.length = 0;
            continue;
          }
          const parsed = parseYamlScalar(valuePart);
            currentBlock.code = typeof parsed === 'string' ? parsed : '';
            continue;
          }

          if (key === 'llm') {
            currentBlock.llm = {
              endpoint: '',
              deploymentName: '',
              apiKey: '',
              promptSource: { type: 'input', value: '' }
            } as LlmConfiguration;
            currentLlmConfig = currentBlock.llm;
            readingLlm = true;
            readingLlmPrompt = false;
            continue;
          }

          assignBlockProperty(currentBlock, key, valuePart);
          arrayTarget = null;
          if (key !== 'position') {
            readingPosition = false;
        }
        continue;
      }

      if (indent === 6 && readingPosition && trimmed.includes(':') && currentBlock.position) {
        const [coordKey, coordValue = ''] = trimmed.split(/:\s*/, 2);
        const numeric = Number(coordValue);
        if (coordKey === 'x' && Number.isFinite(numeric)) {
          currentBlock.position.x = numeric;
        }
        if (coordKey === 'y' && Number.isFinite(numeric)) {
          currentBlock.position.y = numeric;
        }
        continue;
      }

      if (indent >= 6 && arrayTarget && trimmed.startsWith('- ')) {
        const parsed = parseYamlScalar(trimmed.slice(2));
        if (typeof parsed === 'string') {
          (currentBlock[arrayTarget] ??= []).push(parsed);
        } else if (parsed !== null && parsed !== undefined) {
          (currentBlock[arrayTarget] ??= []).push(String(parsed));
        }
        continue;
      }

      continue;
    }

    if (section === 'connections') {
      if (trimmed === '[]') {
        continue;
      }

      if (indent === 2 && trimmed.startsWith('- ')) {
        commitConnection();
        currentConnection = {};
        const remainder = trimmed.slice(2).trim();
        if (remainder.includes(':')) {
          const [key, value] = remainder.split(/:\s*/, 2);
          assignConnectionProperty(currentConnection, key, value);
        }
        continue;
      }

      if (!currentConnection) {
        continue;
      }

      if (indent === 4 && trimmed.includes(':')) {
        const [key, value] = trimmed.split(/:\s*/, 2);
        assignConnectionProperty(currentConnection, key, value);
      }
    }
  }

  if (multilineKey) {
    commitMultiline();
  }

  commitGlobal();
  commitBlock();
  commitConnection();

  try {
    return normalizeWorkflow({
      version: typeof version === 'string' && version.length > 0 ? version : '1.0',
      name: typeof name === 'string' && name.length > 0 ? name : 'Untitled Workflow',
      globals: rawGlobals as GlobalVariable[],
      blocks: rawBlocks as WorkflowBlock[],
      connections: rawConnections as WorkflowConnection[]
    });
  } catch (error) {
    console.error('Unable to parse YAML workflow', error);
    return null;
  }
}

function parseYamlScalar(value: string): unknown {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }

  if (trimmed === 'null' || trimmed === '~') {
    return null;
  }

  if (trimmed === 'true') {
    return true;
  }
  if (trimmed === 'false') {
    return false;
  }

  if (/^[-+]?[0-9]+$/.test(trimmed)) {
    const intValue = Number.parseInt(trimmed, 10);
    if (Number.isSafeInteger(intValue)) {
      return intValue;
    }
  }

  if (/^[-+]?[0-9]*\.[0-9]+$/.test(trimmed)) {
    const floatValue = Number.parseFloat(trimmed);
    if (Number.isFinite(floatValue)) {
      return floatValue;
    }
  }

  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith('\'') && trimmed.endsWith('\''))
  ) {
    try {
      return JSON.parse(trimmed.replace(/'/g, '"'));
    } catch {
      return trimmed.slice(1, -1);
    }
  }

  return trimmed;
}

function assignBlockProperty(block: Partial<WorkflowBlock>, key: string, rawValue: string): void {
  const parsed = parseYamlScalar(rawValue);
  switch (key) {
    case 'id':
      if (typeof parsed === 'string') {
        block.id = parsed;
      }
      break;
    case 'type':
      if (typeof parsed === 'string' && isBlockType(parsed)) {
        block.type = parsed;
      }
      break;
    case 'name':
      if (typeof parsed === 'string') {
        block.name = parsed;
      }
      break;
    default:
      break;
  }
}

function assignGlobalProperty(variable: Partial<GlobalVariable>, key: string, rawValue: string): void {
  const parsed = parseYamlScalar(rawValue);
  if (key === 'name' && typeof parsed === 'string') {
    variable.name = parsed;
  } else if (key === 'value') {
    variable.value = coerceToString(parsed);
  } else if (key === 'description' && typeof parsed === 'string') {
    variable.description = parsed;
  }
}

function assignTransformMappingProperty(mapping: Partial<TransformMapping>, key: string, rawValue: string): void {
  const parsed = parseYamlScalar(rawValue);
  if (typeof parsed !== 'string') {
    return;
  }

  if (key === 'source' || key === 'path' || key === 'from' || key === 'input') {
    mapping.source = parsed;
  } else if (key === 'target' || key === 'name' || key === 'output' || key === 'as') {
    mapping.target = parsed;
  }
}

function assignConnectionProperty(connection: Partial<WorkflowConnection>, key: string, rawValue: string): void {
  const parsed = parseYamlScalar(rawValue);
  if (typeof parsed !== 'string') {
    return;
  }

  if (key === 'from') {
    connection.from = parsed;
  } else if (key === 'to') {
    connection.to = parsed;
  } else if (key === 'fromOutput' || key === 'from_output') {
    connection.fromOutput = parsed;
  }
}

function formatBlockLabel(block: WorkflowBlock): string {
  return block.name.trim().length > 0 ? block.name : BLOCK_TYPE_TITLES[block.type];
}

function WorkflowBuilderFeature(featureContext: FeatureContext) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const canvasInnerRef = useRef<HTMLDivElement | null>(null);
  const {
    apiBaseUrl,
    fallbackBaseUrl,
    userObjectId,
    acquireAccessToken,
    isAuthenticated
  } = featureContext;
  const [initialWorkflow] = useState(() => createInitialWorkflow());
  const [workflowName, setWorkflowName] = useState(initialWorkflow.name);
  const [blocks, setBlocks] = useState<WorkflowBlock[]>(initialWorkflow.blocks);
  const [connections, setConnections] = useState<WorkflowConnection[]>(initialWorkflow.connections);
  const [globalVariables, setGlobalVariables] = useState<GlobalVariableEntry[]>(() =>
    (initialWorkflow.globals ?? []).map((variable) => ({
      id: createGlobalVariableId(),
      name: variable.name,
      value: variable.value,
      description: variable.description
    }))
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [exportPreview, setExportPreview] = useState<ExportPreview | null>(null);
  const [importText, setImportText] = useState('');
  const [savedWorkflows, setSavedWorkflows] = useState<SavedWorkflowSummary[]>([]);
  const [selectedSavedWorkflow, setSelectedSavedWorkflow] = useState('');
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);
  const [isLoadingSavedList, setIsLoadingSavedList] = useState(false);
  const [isLoadingSavedWorkflow, setIsLoadingSavedWorkflow] = useState(false);
  const [pendingConnectionFrom, setPendingConnectionFrom] = useState<{ blockId: string; output?: string } | null>(null);
  const [cursorPosition, setCursorPosition] = useState<Position | null>(null);
  const [dragState, setDragState] = useState<{
    id: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Position>({ x: 0, y: 0 });
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const [isFullscreenPaletteOpen, setIsFullscreenPaletteOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<WorkflowConnection | null>(null);
  const [executionInputText, setExecutionInputText] = useState('{\n  "trigger": "example"\n}');
  const [executionResult, setExecutionResult] = useState('');
  const [verificationResult, setVerificationResult] = useState('');
  const [isExecutingWorkflow, setIsExecutingWorkflow] = useState(false);
  const [isVerifyingWorkflow, setIsVerifyingWorkflow] = useState(false);
  const panDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originPan: Position;
  } | null>(null);
  const connectionDragRef = useRef<{ pointerId: number } | null>(null);
  const skipNextCanvasClickRef = useRef(false);
  const nodeMeasurementsRef = useRef(new Map<string, { width: number; height: number }>());
  const nodeObserversRef = useRef(new Map<string, ResizeObserver>());
  const [, forceNodeMeasurementsUpdate] = useState(0);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!isCanvasFullscreen) {
      document.body.style.removeProperty('overflow');
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCanvasFullscreen]);

  useEffect(() => {
    if (!isCanvasFullscreen) {
      setIsFullscreenPaletteOpen(false);
    }
  }, [isCanvasFullscreen]);

  useEffect(() => {
    return () => {
      for (const observer of nodeObserversRef.current.values()) {
        observer.disconnect();
      }
      nodeObserversRef.current.clear();
      nodeMeasurementsRef.current.clear();
    };
  }, []);

  const nodeElementsRef = useRef(new Map<string, Element | null>());
  const primaryApiBase = useMemo(() => normalizeApiBaseUrl(apiBaseUrl), [apiBaseUrl]);
  const fallbackApiBase = useMemo(() => normalizeApiBaseUrl(fallbackBaseUrl), [fallbackBaseUrl]);
  const shouldAttemptFallback = useMemo(() => {
    return fallbackApiBase.length > 0 && fallbackApiBase !== primaryApiBase;
  }, [fallbackApiBase, primaryApiBase]);

  const clampPan = useCallback((value: Position): Position => {
    return {
      x: Number.isFinite(value.x) ? value.x : 0,
      y: Number.isFinite(value.y) ? value.y : 0
    };
  }, []);

  const clampToCanvas = useCallback((position: Position): Position => {
    return {
      x: Number.isFinite(position.x) ? position.x : 0,
      y: Number.isFinite(position.y) ? position.y : 0
    };
  }, []);

  const registerNodeElement = useCallback(
    (blockId: string, element: HTMLDivElement | null) => {
      const observers = nodeObserversRef.current;
      const previousElement = nodeElementsRef.current.get(blockId);
      if (previousElement === element) {
        return;
      }

      const existingObserver = observers.get(blockId);
      if (existingObserver) {
        existingObserver.disconnect();
        observers.delete(blockId);
      }

      nodeElementsRef.current.set(blockId, element);

      if (!element) {
        return;
      }

      const commitMeasurement = (width: number, height: number) => {
        if (!Number.isFinite(width) || !Number.isFinite(height) || width === 0 || height === 0) {
          return;
        }
        const previous = nodeMeasurementsRef.current.get(blockId);
        const next = { width, height };
        if (previous && Math.abs(previous.width - next.width) < 0.5 && Math.abs(previous.height - next.height) < 0.5) {
          return;
        }
        nodeMeasurementsRef.current.set(blockId, next);
        forceNodeMeasurementsUpdate((value) => value + 1);
      };

      const measureFromElement = () => {
        commitMeasurement(element.offsetWidth, element.offsetHeight);
      };

      measureFromElement();
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) {
          measureFromElement();
          return;
        }

        const borderBoxSize = entry.borderBoxSize?.[0];
        if (borderBoxSize) {
          commitMeasurement(borderBoxSize.inlineSize, borderBoxSize.blockSize);
          return;
        }

        const { width, height } = entry.contentRect;
        if (width || height) {
          commitMeasurement(width, height);
          return;
        }

        measureFromElement();
      });
      observer.observe(element);
      observers.set(blockId, observer);
    },
    [forceNodeMeasurementsUpdate]
  );

  const getNodeSize = useCallback(
    (blockId: string) => nodeMeasurementsRef.current.get(blockId) ?? { width: NODE_WIDTH, height: NODE_HEIGHT },
    []
  );

  const handlePanPointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = panDragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;

      if (!skipNextCanvasClickRef.current && (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2)) {
        skipNextCanvasClickRef.current = true;
      }

      setPan(
        clampPan({
          x: drag.originPan.x + deltaX,
          y: drag.originPan.y + deltaY
        })
      );
    },
    [clampPan]
  );

  const handlePanPointerUp = useCallback(
    (event: PointerEvent) => {
      const drag = panDragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      panDragRef.current = null;
      window.removeEventListener('pointermove', handlePanPointerMove);
      window.removeEventListener('pointerup', handlePanPointerUp);

      if (skipNextCanvasClickRef.current) {
        window.setTimeout(() => {
          skipNextCanvasClickRef.current = false;
        }, 0);
      }
    },
    [handlePanPointerMove]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePanPointerMove);
      window.removeEventListener('pointerup', handlePanPointerUp);
    };
  }, [handlePanPointerMove, handlePanPointerUp]);

  const getCanvasCoordinates = useCallback(
    (clientX: number, clientY: number): Position | null => {
      if (!canvasRef.current) {
        return null;
      }

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const localX = clientX - canvasRect.left;
      const localY = clientY - canvasRect.top;
      const worldX = (localX - pan.x) / zoom - CANVAS_CENTER_X;
      const worldY = (localY - pan.y) / zoom - CANVAS_CENTER_Y;

      return clampToCanvas({
        x: worldX,
        y: worldY
      });
    },
    [clampToCanvas, pan.x, pan.y, zoom]
  );

  const resetMessages = useCallback(() => {
    setStatusMessage(null);
    setErrorMessage(null);
  }, []);

  const workflowApiRequest = useCallback(
    async (path: string, init?: RequestInit, options?: { prefix?: string }) => {
      const headers = new Headers(init?.headers ?? {});
      headers.set('Accept', 'application/json');
      const hasJsonBody = init?.body !== undefined && !(init?.body instanceof FormData);
      if (hasJsonBody) {
        headers.set('Content-Type', 'application/json');
      }
      if (userObjectId) {
        headers.set('X-User-Object-Id', userObjectId);
      }

      if (typeof acquireAccessToken === 'function') {
        try {
          const token = await acquireAccessToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
        } catch (tokenError) {
          console.warn('Unable to acquire access token for workflow API.', tokenError);
        }
      }

      const requestInit: RequestInit = {
        ...init,
        headers,
        credentials: 'include'
      };

      const targetPath = `${options?.prefix ?? WORKFLOW_API_PREFIX}${path}`;

      const attempt = async (base: string) => {
        const url = `${base}${targetPath}`;
        const response = await fetch(url, requestInit);
        if (!response.ok) {
          throw new Error(await parseErrorResponse(response));
        }
        return response.json();
      };

      const baseToUse = primaryApiBase || '';
      try {
        return await attempt(baseToUse);
      } catch (error) {
        if (error instanceof TypeError && shouldAttemptFallback) {
          return attempt(fallbackApiBase);
        }
        throw error instanceof Error ? error : new Error('Workflow API request failed.');
      }
    },
    [acquireAccessToken, fallbackApiBase, primaryApiBase, shouldAttemptFallback, userObjectId]
  );

  const workflowPathSegment = useCallback(() => {
    const baseName = workflowName.trim().length > 0 ? workflowName.trim() : 'Untitled Workflow';
    return baseName.replace(/\s+/g, '_');
  }, [workflowName]);

  const parseExecutionInputs = useCallback((): Record<string, unknown> => {
    const trimmed = executionInputText.trim();
    if (!trimmed) {
      return {};
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch (error) {
      console.error('Invalid execution input JSON.', error);
      throw new Error('Execution inputs must be valid JSON.');
    }
    throw new Error('Execution inputs must be a JSON object.');
  }, [executionInputText]);

  const refreshSavedWorkflows = useCallback(
    async (options?: { suppressErrors?: boolean }) => {
      if (!isAuthenticated || !userObjectId) {
        setSavedWorkflows([]);
        setSelectedSavedWorkflow('');
        return;
      }

      setIsLoadingSavedList(true);
      try {
        const response = (await workflowApiRequest('/workflows')) as { workflows?: SavedWorkflowSummary[] };
        setSavedWorkflows(response.workflows ?? []);
      } catch (error) {
        console.error('Unable to load saved workflows.', error);
        if (!options?.suppressErrors) {
          setErrorMessage(error instanceof Error ? error.message : 'Unable to load saved workflows.');
        }
      } finally {
        setIsLoadingSavedList(false);
      }
    },
    [isAuthenticated, userObjectId, workflowApiRequest]
  );

  useEffect(() => {
    if (!isAuthenticated || !userObjectId) {
      setSavedWorkflows([]);
      setSelectedSavedWorkflow('');
      return;
    }

    void refreshSavedWorkflows({ suppressErrors: true });
  }, [isAuthenticated, refreshSavedWorkflows, userObjectId]);

  const handleConnectionPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!connectionDragRef.current || event.pointerId !== connectionDragRef.current.pointerId) {
        return;
      }
      const point = getCanvasCoordinates(event.clientX, event.clientY);
      if (point) {
        setCursorPosition(point);
      }
    },
    [getCanvasCoordinates]
  );

  const cancelConnectionDrag = useCallback(() => {
    if (!connectionDragRef.current) {
      return;
    }
    connectionDragRef.current = null;
    window.removeEventListener('pointermove', handleConnectionPointerMove);
    window.removeEventListener('pointerup', handleConnectionPointerUp);
    setPendingConnectionFrom(null);
    setCursorPosition(null);
  }, [handleConnectionPointerMove]);

  const handleConnectionPointerUp = useCallback(
    (event: PointerEvent) => {
      if (!connectionDragRef.current || event.pointerId !== connectionDragRef.current.pointerId) {
        return;
      }
      cancelConnectionDrag();
    },
    [cancelConnectionDrag]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleConnectionPointerMove);
      window.removeEventListener('pointerup', handleConnectionPointerUp);
    };
  }, [handleConnectionPointerMove, handleConnectionPointerUp]);

  const startConnectionDrag = useCallback(
    (blockId: string, event: ReactPointerEvent<HTMLButtonElement>, output?: string) => {
      event.preventDefault();
      event.stopPropagation();
      resetMessages();
      setSelectedBlockId(blockId);
      setSelectedConnection(null);
      const point = getCanvasCoordinates(event.clientX, event.clientY);
      if (point) {
        setCursorPosition(point);
      }
      setPendingConnectionFrom({
        blockId,
        output: output?.trim() || undefined
      });
      connectionDragRef.current = { pointerId: event.pointerId };
      window.addEventListener('pointermove', handleConnectionPointerMove);
      window.addEventListener('pointerup', handleConnectionPointerUp);
      setStatusMessage('Drag to another block to complete the connection.');
    },
    [cancelConnectionDrag, getCanvasCoordinates, handleConnectionPointerMove, resetMessages, setStatusMessage]
  );

  useEffect(() => {
    if (!dragState) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const point = getCanvasCoordinates(event.clientX, event.clientY);
      if (!point) {
        return;
      }

      const x = point.x - dragState.offsetX;
      const y = point.y - dragState.offsetY;

      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== dragState.id) {
            return block;
          }
          return {
            ...block,
            position: clampToCanvas({ x, y })
          };
        })
      );
    };

    const handlePointerUp = () => {
      setDragState(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [clampToCanvas, dragState, getCanvasCoordinates]);

  const selectedBlock = useMemo(
    () => blocks.find((block) => block.id === selectedBlockId) ?? null,
    [blocks, selectedBlockId]
  );

  const selectedDefinition = useMemo(
    () => (selectedBlock ? getBlockDefinition(selectedBlock.type) : null),
    [selectedBlock]
  );

  const canEditInputs = useMemo(() => {
    if (!selectedBlock) {
      return false;
    }
    if (selectedBlock.type === 'start') {
      return true;
    }
    return selectedDefinition?.capabilities?.editableInputs !== false;
  }, [selectedBlock, selectedDefinition]);

  const canEditOutputs = useMemo(() => {
    if (!selectedBlock) {
      return false;
    }
    if (selectedBlock.type === 'end') {
      return true;
    }
    return selectedDefinition?.capabilities?.editableOutputs !== false;
  }, [selectedBlock, selectedDefinition]);

  const selectedTransformMappings = useMemo(
    () => (selectedBlock && selectedBlock.type === 'transform' ? getTransformMappings(selectedBlock) : []),
    [selectedBlock]
  );

  const selectedConditionRules = useMemo(
    () => (selectedBlock && selectedBlock.type === 'if' ? getConditionRules(selectedBlock) : []),
    [selectedBlock]
  );

  const selectedLlmConfig = useMemo(
    () => (selectedBlock && selectedBlock.type === 'llm' ? getLlmConfig(selectedBlock) : null),
    [selectedBlock]
  );

  const llmPromptOptions = useMemo(() => {
    if (!selectedBlock || selectedBlock.type !== 'llm') {
      return [] as Array<{ label: string; type: LlmPromptSource['type']; value: string }>;
    }
    const options: Array<{ label: string; type: LlmPromptSource['type']; value: string }> = [];
    for (const input of selectedBlock.inputs) {
      const trimmed = input.trim();
      if (!trimmed) {
        continue;
      }
      options.push({ label: `Input: ${trimmed}`, type: 'input', value: trimmed });
    }
    for (const variable of globalVariables) {
      const name = variable.name.trim();
      if (!name) {
        continue;
      }
      options.push({ label: `Global: ${name}`, type: 'global', value: name });
    }
    return options;
  }, [globalVariables, selectedBlock]);

  const blockMap = useMemo(() => {
    return new Map(blocks.map((block) => [block.id, block]));
  }, [blocks]);

  const selectedConnectionDetails = useMemo(() => {
    if (!selectedConnection) {
      return null;
    }
    const from = blockMap.get(selectedConnection.from);
    const to = blockMap.get(selectedConnection.to);
    if (!from || !to) {
      return null;
    }
    return { connection: selectedConnection, from, to, branchLabel: selectedConnection.fromOutput ?? '' };
  }, [blockMap, selectedConnection]);

  const handleAddGlobalVariable = () => {
    resetMessages();
    setGlobalVariables((previous) => [
      ...previous,
      {
        id: createGlobalVariableId(),
        name: `variable${previous.length + 1}`,
        value: '',
        description: ''
      }
    ]);
  };

  const handleRemoveGlobalVariable = (id: string) => {
    resetMessages();
    setGlobalVariables((previous) => previous.filter((variable) => variable.id !== id));
  };

  const handleGlobalVariableChange = (
    id: string,
    field: 'name' | 'value' | 'description',
    value: string
  ) => {
    setGlobalVariables((previous) =>
      previous.map((variable) =>
        variable.id === id
          ? {
              ...variable,
              [field]: value
            }
          : variable
      )
    );
  };

  const dragImageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = document.createElement('div');
    element.style.width = `${NODE_WIDTH}px`;
    element.style.height = `${NODE_HEIGHT}px`;
    element.style.borderRadius = '16px';
    element.style.background = 'rgba(255,255,255,0.6)';
    element.style.boxShadow = '0 12px 24px rgba(15,23,42,0.18)';
    element.style.border = '1px solid rgba(148, 163, 184, 0.35)';
    element.style.position = 'absolute';
    element.style.top = '-9999px';
    element.style.pointerEvents = 'none';
    document.body.appendChild(element);
    dragImageRef.current = element;
    return () => {
      document.body.removeChild(element);
      dragImageRef.current = null;
    };
  }, []);

  const handlePaletteDragStart = (event: DragEvent<HTMLButtonElement>, type: BlockType) => {
    event.dataTransfer?.setData('application/x-workflow-block-type', type);
    const ghost = dragImageRef.current;
    if (ghost && event.dataTransfer) {
      event.dataTransfer.setDragImage(ghost, NODE_WIDTH / 2, NODE_HEIGHT / 2);
    }
  };

  const handleCanvasDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const type = event.dataTransfer?.getData('application/x-workflow-block-type') as BlockType | '';
    if (!type) {
      return;
    }

    const point = getCanvasCoordinates(event.clientX, event.clientY);
    if (!point) {
      return;
    }

    const position = clampToCanvas({
      x: point.x - NODE_WIDTH / 2,
      y: point.y - NODE_HEIGHT / 2
    });

    createBlock(type, { preferredPosition: position, centerOnBlock: false });
  };

  const fitWorkflowWithinViewport = useCallback(
    (blockList?: WorkflowBlock[]) => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) {
        return;
      }

      const targets = blockList && blockList.length > 0 ? blockList : blocks;
      if (targets.length === 0) {
        setZoom(1);
        setPan(clampPan({ x: 0, y: 0 }));
        return;
      }

      const { width: canvasWidth, height: canvasHeight } = canvasElement.getBoundingClientRect();

      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;

      for (const block of targets) {
        const size = getNodeSize(block.id);
        const left = CANVAS_CENTER_X + block.position.x;
        const top = CANVAS_CENTER_Y + block.position.y;
        const right = left + size.width;
        const bottom = top + size.height;
        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, right);
        maxY = Math.max(maxY, bottom);
      }

      if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
        setZoom(1);
        setPan(clampPan({ x: 0, y: 0 }));
        return;
      }

      minX -= CONNECTOR_VISUAL_PADDING + VIEWPORT_MARGIN;
      maxX += CONNECTOR_VISUAL_PADDING + VIEWPORT_MARGIN;
      minY -= VIEWPORT_MARGIN;
      maxY += VIEWPORT_MARGIN;

      const contentWidth = Math.max(maxX - minX, 1);
      const contentHeight = Math.max(maxY - minY, 1);
      const availableWidth = Math.max(canvasWidth - VIEWPORT_MARGIN * 2, 120);
      const availableHeight = Math.max(canvasHeight - VIEWPORT_MARGIN * 2, 120);
      const fitZoom = Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
      const nextZoom = Math.max(MIN_ZOOM, Math.min(1, fitZoom));
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      setZoom(nextZoom);
      setPan(
        clampPan({
          x: canvasWidth / 2 - centerX * nextZoom,
          y: canvasHeight / 2 - centerY * nextZoom
        })
      );
    },
    [blocks, clampPan, getNodeSize]
  );

  const resetViewport = useCallback(
    (blockList?: WorkflowBlock[]) => {
      fitWorkflowWithinViewport(blockList);
    },
    [fitWorkflowWithinViewport]
  );

  const handleZoomChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = Number(event.target.value);
    if (!Number.isFinite(rawValue)) {
      return;
    }

    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, rawValue / 100));
    const canvasElement = canvasRef.current;

    if (!canvasElement) {
      setZoom(nextZoom);
      return;
    }

    const { width, height } = canvasElement.getBoundingClientRect();
    const anchorX = width / 2;
    const anchorY = height / 2;
    const worldX = (anchorX - pan.x) / zoom;
    const worldY = (anchorY - pan.y) / zoom;

    setZoom(nextZoom);
    setPan(
      clampPan({
        x: anchorX - worldX * nextZoom,
        y: anchorY - worldY * nextZoom
      })
    );
  };

  const handleZoomReset = () => {
    fitWorkflowWithinViewport();
  };

  const hasCenteredInitialViewRef = useRef(false);

  useEffect(() => {
    if (!canvasRef.current || hasCenteredInitialViewRef.current || blocks.length === 0) {
      return;
    }

    hasCenteredInitialViewRef.current = true;
    resetViewport();
  }, [blocks.length, resetViewport]);

  useEffect(() => {
    const preventGestureZoom = (event: Event) => {
      const target = event.target as Node | null;
      if (canvasRef.current && target && canvasRef.current.contains(target)) {
        event.preventDefault();
      }
    };

    const options: AddEventListenerOptions = { passive: false };
    const gestureEvents: Array<'gesturestart' | 'gesturechange' | 'gestureend'> = [
      'gesturestart',
      'gesturechange',
      'gestureend'
    ];
    for (const gestureEvent of gestureEvents) {
      document.addEventListener(gestureEvent, preventGestureZoom, options);
    }

    return () => {
      for (const gestureEvent of gestureEvents) {
        document.removeEventListener(gestureEvent, preventGestureZoom, options);
      }
    };
  }, []);

  const handleCanvasWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!canvasRef.current) {
      return;
    }

    const isPinchGesture = event.ctrlKey || event.metaKey || Math.abs(event.deltaZ) > 0;
    if (!isPinchGesture) {
      // Allow native scrolling for non-zoom gestures.
      return;
    }

    event.preventDefault();
    const zoomFactor = Math.exp(-event.deltaY * 0.002);
    const previousZoom = zoom;
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, previousZoom * zoomFactor));

    if (nextZoom === previousZoom) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;

    setZoom(nextZoom);
    setPan((previousPan) => {
      const worldX = (screenX - previousPan.x) / previousZoom;
      const worldY = (screenY - previousPan.y) / previousZoom;
      return clampPan({
        x: screenX - worldX * nextZoom,
        y: screenY - worldY * nextZoom
      });
    });
  };

  const handleCanvasPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      const targetElement = event.target as HTMLElement | null;
      if (targetElement?.closest('.workflow-builder__node')) {
        return;
      }

      event.preventDefault();
      skipNextCanvasClickRef.current = false;
      panDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originPan: { ...pan }
      };
      window.addEventListener('pointermove', handlePanPointerMove);
      window.addEventListener('pointerup', handlePanPointerUp);
    },
    [handlePanPointerMove, handlePanPointerUp, pan]
  );

  const centerViewportOnBlock = useCallback(
    (block: WorkflowBlock) => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) {
        return;
      }
      const { width, height } = canvasElement.getBoundingClientRect();
      const blockCenterX = CANVAS_CENTER_X + block.position.x + NODE_WIDTH / 2;
      const blockCenterY = CANVAS_CENTER_Y + block.position.y + NODE_HEIGHT / 2;
      setPan(
        clampPan({
          x: width / 2 - blockCenterX,
          y: height / 2 - blockCenterY
        })
      );
    },
    [clampPan, setPan]
  );

  const createBlock = (
    type: BlockType,
    options?: {
      preferredPosition?: Position;
      centerOnBlock?: boolean;
    }
  ) => {
    resetMessages();

    if (type === 'start' && blocks.some((block) => block.type === type)) {
      setErrorMessage(`A workflow can only contain one ${BLOCK_TYPE_TITLES[type]} block.`);
      return;
    }

    const definition = getBlockDefinition(type);
    const existingCount = blocks.filter((block) => block.type === type).length;
    const basePosition = options?.preferredPosition ?? clampToCanvas(autoPosition(blocks.length));
    const name =
      type === 'start'
        ? definition.defaults.name ?? BLOCK_TYPE_TITLES[type]
        : formatNewBlockName(type, existingCount + 1);

    const newBlock: WorkflowBlock = {
      id: createBlockId(type),
      type,
      name,
      position: clampToCanvas(basePosition),
      inputs: [...(definition.defaults.inputs ?? [])],
      outputs: [...(definition.defaults.outputs ?? [])]
    };

    if (definition.capabilities?.code) {
      newBlock.code = definition.defaults.code ?? '';
    }

    if (type === 'transform') {
      newBlock.transformMappings = buildDefaultTransformMappings(newBlock.outputs);
      newBlock.outputs = newBlock.transformMappings.map((mapping) => mapping.target);
    }

    setBlocks((previous) => [...previous, newBlock]);
    setSelectedBlockId(newBlock.id);
    setSelectedConnection(null);
    setStatusMessage(`${BLOCK_TYPE_TITLES[type]} block added to the canvas.`);
    if (options?.centerOnBlock) {
      centerViewportOnBlock(newBlock);
    }
  };

  const handleAddBlockClick = (type: BlockType) => {
    createBlock(type, { centerOnBlock: true });
  };

  const handleCanvasClick = () => {
    if (skipNextCanvasClickRef.current) {
      skipNextCanvasClickRef.current = false;
      return;
    }

    if (pendingConnectionFrom) {
      setPendingConnectionFrom(null);
      setCursorPosition(null);
    } else {
      setSelectedBlockId('');
    }
    setSelectedConnection(null);
  };

  const handleBlockPointerDown = (event: ReactPointerEvent<HTMLDivElement>, blockId: string) => {
    event.stopPropagation();
    if (!canvasInnerRef.current) {
      return;
    }

    const block = blocks.find((item) => item.id === blockId);
    if (!block) {
      return;
    }

    const point = getCanvasCoordinates(event.clientX, event.clientY);
    if (!point) {
      return;
    }

    const offsetX = point.x - block.position.x;
    const offsetY = point.y - block.position.y;

    setSelectedBlockId(blockId);
    setSelectedConnection(null);
    setDragState({ id: blockId, offsetX, offsetY });
  };

  const handleNodeNameChange = useCallback(
    (blockId: string, name: string) => {
      setBlocks((previous) =>
        previous.map((block) => (block.id === blockId ? { ...block, name } : block))
      );
    },
    []
  );

  const handleBlockNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedBlock) {
      return;
    }
    const value = event.target.value;
    setBlocks((previous) =>
      previous.map((block) => (block.id === selectedBlock.id ? { ...block, name: value } : block))
    );
  };

  const handleBlockCodeChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!selectedBlock || !selectedDefinition?.capabilities?.code) {
      return;
    }
    const value = event.target.value;
    setBlocks((previous) =>
      previous.map((block) => (block.id === selectedBlock.id ? { ...block, code: value } : block))
    );
  };

  const updateSelectedIfConditions = useCallback(
    (updater: (rules: ConditionRule[]) => ConditionRule[]) => {
      if (!selectedBlockId) {
        return;
      }
      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId || block.type !== 'if') {
            return block;
          }
          let nextRules = updater(getConditionRules(block));
          if (nextRules.length === 0) {
            nextRules = buildDefaultConditionRules([]);
          }
          nextRules = nextRules.map((rule, index) => ({
            id: rule.id || createConditionRuleId(),
            operator: rule.operator ?? 'equals',
            left: (rule.left ?? '').trim(),
            right:
              rule.operator === 'contains' || rule.operator === 'equals'
                ? (rule.right ?? '').toString()
                : '',
            output: rule.output?.trim() || `branch${index + 1}`
          }));
          return {
            ...block,
            outputs: nextRules.map((rule) => rule.output),
            conditions: nextRules
          };
        })
      );
    },
    [selectedBlockId, setBlocks]
  );

  const handleAddConditionRule = useCallback(() => {
    setBlocks((previous) =>
      previous.map((block) => {
        if (block.id !== selectedBlockId || block.type !== 'if') {
          return block;
        }
        const newCondition: ConditionRule = {
          id: createConditionRuleId(),
          operator: 'equals',
          left: '',
          right: '',
          output: `branch${block.conditions?.length ?? block.outputs.length + 1}`
        };
        const nextConditions = [...getConditionRules(block), newCondition];
        return {
          ...block,
          conditions: nextConditions,
          outputs: nextConditions.map((rule) => rule.output)
        };
      })
    );
  }, [selectedBlockId]);

  const handleRemoveConditionRule = useCallback(
    (index: number) => {
      const removedRule = selectedConditionRules[index];
      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId || block.type !== 'if') {
            return block;
          }
          const nextRules = getConditionRules(block).filter((_, ruleIndex) => ruleIndex !== index);
          if (nextRules.length === 0) {
            return block;
          }
          return {
            ...block,
            conditions: nextRules,
            outputs: nextRules.map((rule) => rule.output)
          };
        })
      );
      if (!removedRule?.output || !selectedBlockId) {
        return;
      }
      setConnections((previous) =>
        previous.filter(
          (connection) =>
            !(
              connection.from === selectedBlockId &&
              (connection.fromOutput ?? '') === removedRule.output
            )
        )
      );
    },
    [selectedBlockId, selectedConditionRules]
  );

  const handleConditionOutputChange = useCallback(
    (index: number, value: string) => {
      const previousLabel = selectedConditionRules[index]?.output ?? '';
      const cleanedValue = value.trim();
      updateSelectedIfConditions((rules) =>
        rules.map((rule, ruleIndex) =>
          ruleIndex === index
            ? {
                ...rule,
                output: cleanedValue
              }
            : rule
        )
      );
      if (!selectedBlockId || !previousLabel) {
        return;
      }
      setConnections((previous) =>
        previous.map((connection) => {
          if (
            connection.from === selectedBlockId &&
            (connection.fromOutput ?? '') === previousLabel
          ) {
            return {
              ...connection,
              fromOutput: cleanedValue || previousLabel
            };
          }
          return connection;
        })
      );
    },
    [selectedBlockId, selectedConditionRules, updateSelectedIfConditions]
  );

  const handleConditionOperatorChange = useCallback(
    (index: number, operator: ConditionOperator) => {
      updateSelectedIfConditions((rules) =>
        rules.map((rule, ruleIndex) =>
          ruleIndex === index
            ? {
                ...rule,
                operator,
                left: operator === 'always' ? '' : rule.left,
                right: operator === 'contains' || operator === 'equals' ? rule.right : ''
              }
            : rule
        )
      );
    },
    [updateSelectedIfConditions]
  );

  const handleConditionLeftChange = useCallback(
    (index: number, value: string) => {
      updateSelectedIfConditions((rules) =>
        rules.map((rule, ruleIndex) =>
          ruleIndex === index
            ? {
                ...rule,
                left: value
              }
            : rule
        )
      );
    },
    [updateSelectedIfConditions]
  );

  const handleConditionRightChange = useCallback(
    (index: number, value: string) => {
      updateSelectedIfConditions((rules) =>
        rules.map((rule, ruleIndex) =>
          ruleIndex === index
            ? {
                ...rule,
                right: value
              }
            : rule
        )
      );
    },
    [updateSelectedIfConditions]
  );

  const handleLlmConfigChange = useCallback(
    (field: keyof LlmConfiguration, value: string) => {
      if (!selectedBlockId) {
        return;
      }
      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId || block.type !== 'llm') {
            return block;
          }
          const current = getLlmConfig(block);
          return {
            ...block,
            llm: {
              ...current,
              [field]: value
            }
          };
        })
      );
    },
    [selectedBlockId, setBlocks]
  );

  const handleLlmPromptSourceChange = useCallback(
    (type: LlmPromptSource['type'], value: string) => {
      if (!selectedBlockId) {
        return;
      }
      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId || block.type !== 'llm') {
            return block;
          }
          const current = getLlmConfig(block);
          return {
            ...block,
            llm: {
              ...current,
              promptSource: {
                type,
                value
              }
            }
          };
        })
      );
    },
    [selectedBlockId, setBlocks]
  );

  const updatePortList = useCallback(
    (portType: 'inputs' | 'outputs', updater: (ports: string[]) => string[]) => {
      if (!selectedBlockId) {
        return;
      }

      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId) {
            return block;
          }

          if (block.type === 'transform' && portType === 'outputs') {
            const existingMappings = getTransformMappings(block);
            const nextOutputs = updater(existingMappings.map((mapping) => mapping.target));
            const nextMappings = nextOutputs.map((target, index) => {
              const existing = existingMappings[index];
              const nextTarget = target.trim();
              const fallbackName = `output${index + 1}`;
              return {
                source: existing?.source ?? (nextTarget || fallbackName),
                target: nextTarget || fallbackName
              };
            });

            return {
              ...block,
              outputs: nextMappings.map((mapping) => mapping.target),
              transformMappings: nextMappings
            };
          }

          if (block.type === 'if' && portType === 'outputs') {
            const existingRules = getConditionRules(block);
            const nextOutputs = updater(existingRules.map((rule) => rule.output));
            const nextRules = nextOutputs.map((output, index) => {
              const existing = existingRules[index];
              const cleanedOutput = output.trim() || `branch${index + 1}`;
              return {
                id: existing?.id ?? createConditionRuleId(),
                operator: existing?.operator ?? 'equals',
                left: existing?.left ?? '',
                right: existing?.right ?? '',
                output: cleanedOutput
              };
            });
            return {
              ...block,
              outputs: nextRules.map((rule) => rule.output),
              conditions: nextRules
            };
          }

          const nextPorts = updater([...block[portType]]);
          return {
            ...block,
            [portType]: nextPorts
          };
        })
      );
    },
    [selectedBlockId]
  );

  const handlePortChange = useCallback(
    (portType: 'inputs' | 'outputs', index: number, value: string) => {
      updatePortList(portType, (ports) => {
        if (index < 0 || index >= ports.length) {
          return ports;
        }
        ports[index] = value;
        return ports;
      });
    },
    [updatePortList]
  );

  const handleAddPort = useCallback(
    (portType: 'inputs' | 'outputs') => {
      updatePortList(portType, (ports) => {
        const labelBase = portType === 'inputs' ? 'input' : 'output';
        const nextLabel = `${labelBase}${ports.length + 1}`;
        return [...ports, nextLabel];
      });
    },
    [updatePortList]
  );

  const handleRemovePort = useCallback(
    (portType: 'inputs' | 'outputs', index: number) => {
      updatePortList(portType, (ports) => ports.filter((_, portIndex) => portIndex !== index));
    },
    [updatePortList]
  );

  const handleTransformSourceChange = useCallback(
    (index: number, value: string) => {
      if (!selectedBlockId) {
        return;
      }

      setBlocks((previous) =>
        previous.map((block) => {
          if (block.id !== selectedBlockId || block.type !== 'transform') {
            return block;
          }

          const existingMappings = getTransformMappings(block);
          if (index < 0 || index >= existingMappings.length) {
            return block;
          }

          const nextMappings = existingMappings.map((mapping, mappingIndex) =>
            mappingIndex === index ? { ...mapping, source: value } : mapping
          );

          return {
            ...block,
            transformMappings: nextMappings,
            outputs: nextMappings.map((mapping) => mapping.target)
          };
        })
      );
    },
    [selectedBlockId]
  );

  const handleDeleteBlock = () => {
    if (!selectedBlock) {
      return;
    }

    if (selectedBlock.type === 'start') {
      setErrorMessage(`The ${BLOCK_TYPE_TITLES[selectedBlock.type]} block cannot be removed.`);
      return;
    }

    setBlocks((previous) => previous.filter((block) => block.id !== selectedBlock.id));
    setConnections((previous) => previous.filter((connection) => connection.from !== selectedBlock.id && connection.to !== selectedBlock.id));
    setSelectedBlockId('');
    setSelectedConnection(null);
    setStatusMessage(`${selectedBlock.name} removed from the workflow.`);
  };

  const handleCompleteConnection = (targetId: string) => {
    resetMessages();
    if (!pendingConnectionFrom) {
      return;
    }

    const sourceId = pendingConnectionFrom.blockId;
    const sourceOutput = pendingConnectionFrom.output?.trim() || undefined;

    if (sourceId === targetId) {
      setErrorMessage('A block cannot connect to itself.');
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      return;
    }

    const fromBlock = blockMap.get(sourceId);
    const targetBlock = blockMap.get(targetId);
    if (!fromBlock || !targetBlock) {
      setErrorMessage('One or more selected blocks no longer exists.');
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      return;
    }

    if (targetBlock.type === 'start') {
      setErrorMessage('The Start block cannot accept incoming connections.');
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      return;
    }

    if (fromBlock.type === 'end') {
      setErrorMessage('Connections cannot originate from the End block.');
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      return;
    }

    if (
      connections.some(
        (connection) =>
          connection.from === sourceId &&
          connection.to === targetId &&
          (connection.fromOutput ?? '') === (sourceOutput ?? '')
      )
    ) {
      setStatusMessage('That connection already exists.');
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      return;
    }

    const newConnection: WorkflowConnection = {
      from: sourceId,
      to: targetId,
      ...(sourceOutput ? { fromOutput: sourceOutput } : {})
    };
    setConnections((previous) => [...previous, newConnection]);
    setSelectedConnection(newConnection);
    setPendingConnectionFrom(null);
    setCursorPosition(null);
    cancelConnectionDrag();
    setStatusMessage('Connection added to the workflow.');
  };

  const handleRemoveConnection = (connection: WorkflowConnection) => {
    setConnections((previous) =>
      previous.filter(
        (item) =>
          !(
            item.from === connection.from &&
            item.to === connection.to &&
            (item.fromOutput ?? '') === (connection.fromOutput ?? '')
          )
      )
    );
    setSelectedConnection((current) => {
      if (
        current &&
        current.from === connection.from &&
        current.to === connection.to &&
        (current.fromOutput ?? '') === (connection.fromOutput ?? '')
      ) {
        return null;
      }
      return current;
    });
    setStatusMessage('Connection removed.');
  };

  const handleSelectConnection = useCallback(
    (connection: WorkflowConnection) => {
      cancelConnectionDrag();
      setSelectedBlockId('');
      setSelectedConnection(connection);
    },
    [cancelConnectionDrag]
  );

  const buildDocument = useCallback((): WorkflowDocument => {
    return normalizeWorkflow({
      version: '1.0',
      name: workflowName.trim().length > 0 ? workflowName.trim() : 'Untitled Workflow',
      blocks,
      connections,
      globals: globalVariables.map((variable, index) => {
        const name = variable.name.trim();
        const description = variable.description?.trim() ?? '';
        return {
          name: name.length > 0 ? name : `variable${index + 1}`,
          value: variable.value,
          ...(description.length > 0 ? { description } : {})
        };
      })
    });
  }, [blocks, connections, globalVariables, workflowName]);

  const handleVerifyWorkflow = useCallback(async () => {
    resetMessages();
    let parsedInputs: Record<string, unknown> = {};
    try {
      parsedInputs = parseExecutionInputs();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Execution inputs must be valid JSON.');
      return;
    }
    const document = buildDocument();
    setIsVerifyingWorkflow(true);
    setVerificationResult('');
    try {
      const response = (await workflowApiRequest(
        `/verifyworkflow/${encodeURIComponent(workflowPathSegment())}`,
        {
          method: 'POST',
          body: JSON.stringify({ document, inputs: parsedInputs })
        },
        { prefix: WORKFLOW_EXECUTION_API_PREFIX }
      )) as { is_valid?: boolean; errors?: string[] };
      const errors = response.errors ?? [];
      if (response.is_valid || errors.length === 0) {
        setVerificationResult('Workflow is valid and ready to execute.');
        setStatusMessage('Workflow validation succeeded.');
      } else {
        const combined = errors.join('\n') || 'Workflow validation failed.';
        setVerificationResult(combined);
        setErrorMessage(errors[0] ?? 'Workflow validation failed.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to verify workflow.');
    } finally {
      setIsVerifyingWorkflow(false);
    }
  }, [
    buildDocument,
    parseExecutionInputs,
    resetMessages,
    workflowApiRequest,
    workflowPathSegment
  ]);

  const handleExecuteWorkflow = useCallback(async () => {
    resetMessages();
    let parsedInputs: Record<string, unknown> = {};
    try {
      parsedInputs = parseExecutionInputs();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Execution inputs must be valid JSON.');
      return;
    }

    const document = buildDocument();
    setIsExecutingWorkflow(true);
    setExecutionResult('');
    try {
      const response = (await workflowApiRequest(
        `/executeworkflow/${encodeURIComponent(workflowPathSegment())}`,
        {
          method: 'POST',
          body: JSON.stringify({ document, inputs: parsedInputs })
        },
        { prefix: WORKFLOW_EXECUTION_API_PREFIX }
      )) as { status?: string; outputs?: Record<string, unknown>; logs?: string[]; errors?: string[] };
      setExecutionResult(JSON.stringify(response, null, 2));
      if (response.status === 'success') {
        setStatusMessage('Workflow executed successfully.');
      } else if (response.errors && response.errors.length > 0) {
        setErrorMessage(response.errors[0]);
      } else {
        setErrorMessage('Workflow execution failed.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to execute workflow.');
    } finally {
      setIsExecutingWorkflow(false);
    }
  }, [
    buildDocument,
    parseExecutionInputs,
    resetMessages,
    workflowApiRequest,
    workflowPathSegment
  ]);

  const handleSaveWorkflow = useCallback(async () => {
    resetMessages();
    if (!isAuthenticated || !userObjectId) {
      setErrorMessage('Sign in to save workflows to Cosmos DB.');
      return;
    }

    setIsSavingWorkflow(true);
    try {
      const workflowDocument = buildDocument();
      const payload = {
        name: workflowDocument.name,
        document: workflowDocument,
        exportJson: exportToJson(workflowDocument),
        exportYaml: exportToYaml(workflowDocument)
      };
      const response = (await workflowApiRequest('/workflows', {
        method: 'POST',
        body: JSON.stringify(payload)
      })) as { workflow: SavedWorkflowDetail };
      setStatusMessage(`Saved workflow "${response.workflow.name}" to Cosmos DB.`);
      setSelectedSavedWorkflow(response.workflow.name);
      await refreshSavedWorkflows({ suppressErrors: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to save workflow.');
    } finally {
      setIsSavingWorkflow(false);
    }
  }, [
    buildDocument,
    isAuthenticated,
    refreshSavedWorkflows,
    resetMessages,
    userObjectId,
    workflowApiRequest
  ]);

  const applyWorkflowDocument = useCallback(
    (document: WorkflowDocument, sourceDescription?: string) => {
      setWorkflowName(document.name);
      setBlocks(
        document.blocks.map((block) => ({
          ...block,
          position: clampToCanvas(block.position)
        }))
      );
      setConnections(document.connections);
      setGlobalVariables(
        (document.globals ?? []).map((variable) => ({
          id: createGlobalVariableId(),
          name: variable.name,
          value: variable.value,
          description: variable.description ?? ''
        }))
      );
      setSelectedBlockId(document.blocks[0]?.id ?? '');
      setSelectedConnection(null);
      setPendingConnectionFrom(null);
      setCursorPosition(null);
      setExportPreview(null);
      setZoom(1);
      const sourceLabel = sourceDescription ? ` from ${sourceDescription}` : '';
      setStatusMessage(`Loaded workflow "${document.name}"${sourceLabel}.`);
    },
    [clampToCanvas]
  );

  const handleLoadSavedWorkflow = useCallback(async () => {
    resetMessages();
    if (!isAuthenticated || !userObjectId) {
      setErrorMessage('Sign in to load saved workflows.');
      return;
    }
    if (!selectedSavedWorkflow) {
      setErrorMessage('Select a saved workflow to load.');
      return;
    }

    setIsLoadingSavedWorkflow(true);
    try {
      const encodedName = encodeURIComponent(selectedSavedWorkflow);
      const response = (await workflowApiRequest(`/workflows/${encodedName}`)) as { workflow: SavedWorkflowDetail };
      applyWorkflowDocument(response.workflow.document, 'Cosmos DB');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to load that workflow.');
    } finally {
      setIsLoadingSavedWorkflow(false);
    }
  }, [
    applyWorkflowDocument,
    isAuthenticated,
    resetMessages,
    selectedSavedWorkflow,
    userObjectId,
    workflowApiRequest
  ]);

  const handleExport = (format: 'json' | 'yaml') => {
    resetMessages();
    const workflowDocument = buildDocument();
    const content = format === 'json' ? exportToJson(workflowDocument) : exportToYaml(workflowDocument);
    const mimeType = format === 'json' ? 'application/json' : 'text/yaml';
    const extension = format === 'json' ? 'json' : 'yaml';
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);

    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = `${workflowDocument.name.replace(/\s+/g, '-').toLowerCase() || 'workflow'}.${extension}`;
    anchor.click();
    URL.revokeObjectURL(url);

    setExportPreview({ format, content });
    setStatusMessage(`Workflow exported as ${format.toUpperCase()}.`);
  };

  const handleLoadFile = (event: ChangeEvent<HTMLInputElement>) => {
    resetMessages();
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      let parsed: WorkflowDocument | null = null;
      if (/\.ya?ml$/i.test(file.name)) {
        parsed = parseWorkflowYaml(result);
      }
      if (!parsed) {
        parsed = parseWorkflowJson(result);
      }
      if (!parsed) {
        parsed = parseWorkflowYaml(result);
      }

      if (!parsed) {
        setErrorMessage('Unable to read that workflow file. Please verify it contains valid JSON or YAML.');
        return;
      }

      applyWorkflowDocument(parsed, `file "${file.name}"`);
    };
    reader.onerror = () => {
      setErrorMessage('Unable to read the selected file.');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImportText = () => {
    resetMessages();
    const text = importText.trim();
    if (!text) {
      setErrorMessage('Paste a JSON or YAML workflow before importing.');
      return;
    }

    let parsed: WorkflowDocument | null = null;
    parsed = parseWorkflowJson(text);
    if (!parsed) {
      parsed = parseWorkflowYaml(text);
    }

    if (!parsed) {
      setErrorMessage('Unable to understand the pasted workflow. Please ensure it is valid JSON or YAML.');
      return;
    }

    applyWorkflowDocument(parsed, 'pasted content');
    setImportText('');
  };

  const handleReset = () => {
    const fresh = createInitialWorkflow();
    setWorkflowName(fresh.name);
    setBlocks(fresh.blocks);
    setConnections(fresh.connections);
    setGlobalVariables(
      (fresh.globals ?? []).map((variable) => ({
        id: createGlobalVariableId(),
        name: variable.name,
        value: variable.value,
        description: variable.description ?? ''
      }))
    );
    setSelectedBlockId('');
    setPendingConnectionFrom(null);
    setCursorPosition(null);
    setExportPreview(null);
    setImportText('');
    setStatusMessage('Workflow reset to default Start and End blocks.');
    setErrorMessage(null);
    setSelectedConnection(null);
    resetViewport();
    setIsCanvasFullscreen(false);
  };

  const connectionLines = useMemo(() => {
    const lines: Array<{
      key: string;
      path: string;
      fromId: string;
      toId: string;
      fromOutput?: string;
      midX: number;
      midY: number;
    }> = [];
    for (const connection of connections) {
      const from = blockMap.get(connection.from);
      const to = blockMap.get(connection.to);
      if (!from || !to) {
        continue;
      }

      const fromSize = getNodeSize(from.id);
      const toSize = getNodeSize(to.id);
      const startRatio = getOutputHandleRatio(from, connection.fromOutput);
      const startX =
        CANVAS_CENTER_X + from.position.x + fromSize.width + CONNECTOR_OFFSET_X + CONNECTOR_GAP + CONNECTOR_LINE_PADDING;
      const startY = CANVAS_CENTER_Y + from.position.y + fromSize.height * startRatio;
      const endX =
        CANVAS_CENTER_X + to.position.x - CONNECTOR_OFFSET_X - CONNECTOR_GAP - CONNECTOR_LINE_PADDING;
      const endY = CANVAS_CENTER_Y + to.position.y + toSize.height / 2;
      const deltaX = endX - startX;
      const controlOffset = Math.max(Math.abs(deltaX) / 2, 120);
      const firstControl = startX + controlOffset;
      const secondControl = endX - controlOffset;
      const path = `M${startX},${startY} C${firstControl},${startY} ${secondControl},${endY} ${endX},${endY}`;
      lines.push({
        key: `${connection.from}-${connection.fromOutput ?? 'default'}-${connection.to}`,
        path,
        fromId: connection.from,
        fromOutput: connection.fromOutput,
        toId: connection.to,
        midX: (startX + endX) / 2,
        midY: (startY + endY) / 2
      });
    }
    return lines;
  }, [blockMap, connections, getNodeSize]);

  const pendingPath = useMemo(() => {
    if (!pendingConnectionFrom || !cursorPosition) {
      return null;
    }
    const fromBlock = blockMap.get(pendingConnectionFrom.blockId);
    if (!fromBlock || !canvasInnerRef.current) {
      return null;
    }

    const fromSize = getNodeSize(fromBlock.id);
    const startRatio = getOutputHandleRatio(fromBlock, pendingConnectionFrom.output);
    const startX =
      CANVAS_CENTER_X + fromBlock.position.x + fromSize.width + CONNECTOR_OFFSET_X + CONNECTOR_GAP + CONNECTOR_LINE_PADDING;
    const startY = CANVAS_CENTER_Y + fromBlock.position.y + fromSize.height * startRatio;
    const endX =
      CANVAS_CENTER_X + cursorPosition.x - CONNECTOR_OFFSET_X - CONNECTOR_GAP - CONNECTOR_LINE_PADDING;
    const endY = CANVAS_CENTER_Y + cursorPosition.y;
    const deltaX = endX - startX;
    const controlOffset = Math.max(Math.abs(deltaX) / 2, 120);
    const firstControl = startX + controlOffset;
    const secondControl = endX - controlOffset;
    return `M${startX},${startY} C${firstControl},${startY} ${secondControl},${endY} ${endX},${endY}`;
  }, [blockMap, cursorPosition, getNodeSize, pendingConnectionFrom]);

  const canvasGridStyle = useMemo(
    () =>
      ({
        '--grid-offset-x': `${pan.x}px`,
        '--grid-offset-y': `${pan.y}px`,
        '--grid-size': `${GRID_SIZE * zoom}px`
      }) as CSSProperties,
    [pan.x, pan.y, zoom]
  );

  return (
    <section className="page-section workflow-builder" aria-labelledby="workflow-builder-heading">
      <div className="workflow-builder__header">
        <div>
          <h2 id="workflow-builder-heading">Workflow Builder</h2>
          <p className="workflow-builder__subtitle">
            Arrange Start, Code, Transform, and End blocks on the canvas to craft branching AI workflows reminiscent of n8n or the OpenAI agent builder.
            Configure inputs and outputs for each node, reshape data along the way, and round-trip your design through JSON or YAML blueprints.
          </p>
        </div>
        <div className="workflow-builder__actions">
          <button type="button" className="secondary-button" onClick={handleReset}>
            Reset workflow
          </button>
        </div>
      </div>

      {(statusMessage || errorMessage) && (
        <div className={`workflow-builder__alert${errorMessage ? ' workflow-builder__alert--error' : ''}`} role="status">
          {errorMessage ?? statusMessage}
        </div>
      )}

      <div className="workflow-builder__layout">
        <aside className="workflow-builder__sidebar">
          <div className="workflow-builder__form-group">
            <label htmlFor="workflow-name">Workflow name</label>
            <input
              id="workflow-name"
              type="text"
              value={workflowName}
              onChange={(event) => setWorkflowName(event.target.value)}
            />
          </div>

          <div className="workflow-builder__panel workflow-builder__persistence">
            <div>
              <h3>Cosmos DB sync</h3>
              <p className="workflow-builder__persistence-hint">
                Save personal workflows to Azure Cosmos DB and load them on any device.
              </p>
            </div>
            <div className="workflow-builder__persistence-actions">
              <button
                type="button"
                className="primary-button"
                onClick={handleSaveWorkflow}
                disabled={!isAuthenticated || isSavingWorkflow}
              >
                {isSavingWorkflow ? 'Saving…' : 'Save workflow'}
              </button>
              <button
                type="button"
                className="ghost-button"
                onClick={() => refreshSavedWorkflows()}
                disabled={!isAuthenticated || isLoadingSavedList}
              >
                {isLoadingSavedList ? 'Refreshing…' : 'Refresh list'}
              </button>
            </div>
            {isAuthenticated ? (
              <>
                <div className="workflow-builder__form-group">
                  <label htmlFor="saved-workflow-select">Saved workflows</label>
                  <select
                    id="saved-workflow-select"
                    value={selectedSavedWorkflow}
                    onChange={(event) => setSelectedSavedWorkflow(event.target.value)}
                    disabled={isLoadingSavedList}
                  >
                    <option value="">Choose a workflow</option>
                    {savedWorkflows.map((workflow) => (
                      <option key={workflow.name} value={workflow.name}>
                        {workflow.name} — {new Date(workflow.updatedAt).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
                {savedWorkflows.length === 0 && !isLoadingSavedList && (
                  <p className="workflow-builder__persistence-hint">No saved workflows yet.</p>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleLoadSavedWorkflow}
                  disabled={!selectedSavedWorkflow || isLoadingSavedWorkflow}
                >
                  {isLoadingSavedWorkflow ? 'Loading…' : 'Load workflow'}
                </button>
              </>
            ) : (
              <p className="workflow-builder__persistence-hint">
                Sign in to sync workflows across browsers via Cosmos DB.
              </p>
            )}
          </div>

          <div className="workflow-builder__palette">
            <div className="workflow-builder__palette-header">
              <h3>Block palette</h3>
              <p>Add blocks by clicking or drag them onto the canvas.</p>
            </div>
            <div className="workflow-builder__palette-items">
              {BLOCK_TYPES.map((type) => {
                const definition = getBlockDefinition(type);
                return (
                  <button
                    key={type}
                    type="button"
                    className={`workflow-builder__palette-item workflow-builder__palette-item--${type}`}
                    onClick={() => handleAddBlockClick(type)}
                    draggable
                    onDragStart={(event) => handlePaletteDragStart(event, type)}
                    title={definition.description}
                  >
                    {definition.title}
                  </button>
                );
              })}
            </div>
            <p className="workflow-builder__palette-hint">
              Use the handles on each block to create connections. Start blocks can only be added once per workflow.
            </p>
          </div>

          <div className="workflow-builder__import">
            <h3>Import workflow</h3>
            <p>Bring an exported JSON or YAML blueprint back into the canvas.</p>
            <div className="workflow-builder__import-actions">
              <label className="workflow-builder__file-button">
                <input type="file" accept=".json,.yaml,.yml" onChange={handleLoadFile} />
                Load from file
              </label>
              <button type="button" className="secondary-button" onClick={handleImportText}>
                Load pasted text
              </button>
            </div>
            <textarea
              className="workflow-builder__import-textarea"
              rows={4}
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
              placeholder="Paste JSON or YAML here to import a saved workflow."
            />
            <p className="workflow-builder__details-hint">Supports both JSON and YAML. Format detection is automatic.</p>
          </div>

          <div className="workflow-builder__globals">
            <h3>Global variables</h3>
            <p>Define constants, prompts, or other shared values available to every block.</p>
            {globalVariables.length === 0 ? (
              <p className="workflow-builder__details-hint">No global variables defined yet.</p>
            ) : (
              <div className="workflow-builder__globals-list">
                {globalVariables.map((variable) => (
                  <div key={variable.id} className="workflow-builder__globals-item">
                    <div className="workflow-builder__globals-item-header">
                      <div className="workflow-builder__globals-field workflow-builder__globals-field--inline">
                        <label htmlFor={`global-name-${variable.id}`}>Name</label>
                        <input
                          id={`global-name-${variable.id}`}
                          type="text"
                          value={variable.name}
                          onChange={(event) => handleGlobalVariableChange(variable.id, 'name', event.target.value)}
                          placeholder="e.g. systemPrompt"
                        />
                      </div>
                      <button
                        type="button"
                        className="ghost-button workflow-builder__globals-remove"
                        onClick={() => handleRemoveGlobalVariable(variable.id)}
                        aria-label={`Remove global variable ${variable.name || 'entry'}`}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="workflow-builder__globals-field-group">
                      <div className="workflow-builder__globals-field workflow-builder__globals-field--stacked">
                        <label htmlFor={`global-value-${variable.id}`}>Value</label>
                        <textarea
                          id={`global-value-${variable.id}`}
                          rows={3}
                          value={variable.value}
                          onChange={(event) => handleGlobalVariableChange(variable.id, 'value', event.target.value)}
                          placeholder="Enter the default content or payload for this variable"
                        />
                      </div>
                      <div className="workflow-builder__globals-field workflow-builder__globals-field--stacked">
                        <label htmlFor={`global-description-${variable.id}`}>Description (optional)</label>
                        <input
                          id={`global-description-${variable.id}`}
                          type="text"
                          value={variable.description ?? ''}
                          onChange={(event) => handleGlobalVariableChange(variable.id, 'description', event.target.value)}
                          placeholder="Notes about how this variable is used"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="secondary-button" onClick={handleAddGlobalVariable}>
              Add variable
            </button>
          </div>

          <div className="workflow-builder__connections-panel">
            <h3>Connections</h3>
            {connections.length === 0 ? (
              <p className="workflow-builder__connections-empty">No connections yet. Click a block&apos;s output handle to start one.</p>
            ) : (
              <ul className="workflow-builder__connections-list">
                {connections.map((connection) => {
                  const isSelected =
                    selectedConnection?.from === connection.from &&
                    selectedConnection?.to === connection.to &&
                    (selectedConnection?.fromOutput ?? '') === (connection.fromOutput ?? '');
                  const from = blockMap.get(connection.from);
                  const to = blockMap.get(connection.to);
                  const branchLabel = connection.fromOutput ?? '';
                  return (
                    <li
                      key={`${connection.from}-${branchLabel || 'default'}-${connection.to}`}
                      className={isSelected ? 'workflow-builder__connections-item workflow-builder__connections-item--selected' : 'workflow-builder__connections-item'}
                      onClick={() => handleSelectConnection(connection)}
                    >
                      <div className="workflow-builder__connections-summary">
                        <strong>{from ? formatBlockLabel(from) : connection.from}</strong>
                        {branchLabel && <span className="workflow-builder__connection-branch">{branchLabel}</span>}
                        <span aria-hidden="true">→</span>
                        <strong>{to ? formatBlockLabel(to) : connection.to}</strong>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemoveConnection(connection);
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="workflow-builder__export">
            <h3>Export workflow</h3>
            <div className="workflow-builder__export-buttons">
              <button type="button" onClick={() => handleExport('json')}>
                Export JSON
              </button>
              <button type="button" onClick={() => handleExport('yaml')}>
                Export YAML
              </button>
            </div>
            {exportPreview && (
              <div className="workflow-builder__preview">
                <div className="workflow-builder__preview-header">
                  <h4>{exportPreview.format.toUpperCase()} preview</h4>
                  <button type="button" onClick={() => setExportPreview(null)}>
                    Clear
                  </button>
                </div>
                <pre>{exportPreview.content}</pre>
              </div>
            )}
          </div>
          <div className="workflow-builder__execution-panel">
            <h3>Run workflow</h3>
            <p className="workflow-builder__execution-hint">Provide JSON inputs for the Start block, verify the workflow, or run it via the new API.</p>
            <label className="workflow-builder__execution-label" htmlFor="workflow-execution-input">
              Execution inputs (JSON)
            </label>
            <textarea
              id="workflow-execution-input"
              rows={4}
              value={executionInputText}
              onChange={(event) => setExecutionInputText(event.target.value)}
            />
            <div className="workflow-builder__execution-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={handleVerifyWorkflow}
                disabled={isVerifyingWorkflow || isExecutingWorkflow}
              >
                {isVerifyingWorkflow ? 'Verifying…' : 'Verify'}
              </button>
              <button type="button" onClick={handleExecuteWorkflow} disabled={isExecutingWorkflow}>
                {isExecutingWorkflow ? 'Executing…' : 'Execute'}
              </button>
            </div>
            {verificationResult && (
              <p className="workflow-builder__execution-status">{verificationResult}</p>
            )}
            {executionResult && (
              <div className="workflow-builder__execution-output">
                <h4>Execution output</h4>
                <pre>{executionResult}</pre>
              </div>
            )}
          </div>
        </aside>

        <div className="workflow-builder__main">
          {isCanvasFullscreen && <div className="workflow-builder__canvas-backdrop" aria-hidden="true" />}
          <div className={`workflow-builder__canvas-panel${
            isCanvasFullscreen ? ' workflow-builder__canvas-panel--fullscreen' : ''
          }`}>
            <div className="workflow-builder__canvas-toolbar" role="group" aria-label="Canvas controls">
              <label htmlFor="workflow-zoom">Zoom</label>
              <input
                id="workflow-zoom"
                type="range"
                min={Math.round(MIN_ZOOM * 100)}
                max={Math.round(MAX_ZOOM * 100)}
                value={Math.round(zoom * 100)}
                onChange={handleZoomChange}
              />
              <span className="workflow-builder__zoom-value">{Math.round(zoom * 100)}%</span>
              <button type="button" className="ghost-button" onClick={() => setIsCanvasFullscreen((previous) => !previous)}>
                {isCanvasFullscreen ? 'Exit full screen' : 'Full screen'}
              </button>
              {isCanvasFullscreen && (
                <button
                  type="button"
                  className={`ghost-button workflow-builder__palette-toggle${isFullscreenPaletteOpen ? ' is-open' : ''}`}
                  onClick={() => setIsFullscreenPaletteOpen((previous) => !previous)}
                >
                  {isFullscreenPaletteOpen ? 'Hide palette' : 'Block palette'}
                </button>
              )}
              <button type="button" onClick={handleZoomReset} className="secondary-button">
                Reset view
              </button>
            </div>
            <div
              className={`workflow-builder__workspace${
                selectedBlock ? ' workflow-builder__workspace--inspector' : ''
              }`}
            >
              <div
                className={`workflow-builder__canvas${
                  pendingConnectionFrom ? ' workflow-builder__canvas--connecting' : ''
                }`}
                ref={canvasRef}
                style={canvasGridStyle}
                onPointerDown={handleCanvasPointerDown}
                onClick={handleCanvasClick}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleCanvasDrop}
                onWheel={handleCanvasWheel}
                onPointerMove={(event) => {
                  if (!pendingConnectionFrom) {
                    return;
                  }
                  const point = getCanvasCoordinates(event.clientX, event.clientY);
                  if (!point) {
                    return;
                  }
                  setCursorPosition(point);
                }}
                onPointerLeave={() => {
                  if (pendingConnectionFrom) {
                    setCursorPosition(null);
                  }
                }}
              >
                {!selectedBlock && (
                  <div className="workflow-builder__canvas-hint">
                    Click a block to open the inspector, or drag one from the palette to begin.
                  </div>
                )}
                <div
                  className="workflow-builder__canvas-inner"
                  ref={canvasInnerRef}
                  style={{
                    width: CANVAS_WIDTH,
                    height: CANVAS_HEIGHT,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: '0 0'
                  }}
                >
                  <svg
                    className="workflow-builder__connection-layer"
                    role="presentation"
                    width={CANVAS_WIDTH}
                    height={CANVAS_HEIGHT}
                    viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
                  >
                    {connectionLines.map((line) => {
                      const isSelected =
                        selectedConnection?.from === line.fromId &&
                        selectedConnection?.to === line.toId &&
                        (selectedConnection?.fromOutput ?? '') === (line.fromOutput ?? '');
                      return (
                        <path
                          key={line.key}
                          className={`workflow-builder__connection-line${
                            isSelected ? ' workflow-builder__connection-line--selected' : ''
                          }`}
                          d={line.path}
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            handleSelectConnection({ from: line.fromId, fromOutput: line.fromOutput, to: line.toId });
                          }}
                          onPointerUp={(event) => {
                            event.stopPropagation();
                            handleSelectConnection({ from: line.fromId, fromOutput: line.fromOutput, to: line.toId });
                          }}
                        />
                      );
                    })}
                    {pendingPath && (
                      <path className="workflow-builder__connection-line workflow-builder__connection--pending" d={pendingPath} />
                    )}
                  </svg>
                  {blocks.map((block) => {
                    const definition = getBlockDefinition(block.type);
                    const hasCode = Boolean(definition.capabilities?.code);
                    const allowsIncomingConnections = block.type !== 'start';
                    const allowsOutgoingConnections = block.type !== 'end';
                    return (
                      <div
                        key={block.id}
                        className={`workflow-builder__node workflow-builder__node--${block.type}${
                          selectedBlockId === block.id ? ' workflow-builder__node--selected' : ''
                        }${pendingConnectionFrom?.blockId === block.id ? ' workflow-builder__node--pending' : ''}`}
                        style={{
                          left: CANVAS_CENTER_X + block.position.x,
                          top: CANVAS_CENTER_Y + block.position.y
                        }}
                        onPointerDown={(event) => handleBlockPointerDown(event, block.id)}
                      >
                        {allowsIncomingConnections && (
                          <button
                            type="button"
                            className="workflow-builder__connector workflow-builder__connector--input"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              setSelectedBlockId(block.id);
                              setSelectedConnection(null);
                            }}
                            onPointerUp={(event) => {
                              event.stopPropagation();
                              if (pendingConnectionFrom) {
                                handleCompleteConnection(block.id);
                              }
                            }}
                            aria-label={`Complete connection to ${formatBlockLabel(block)}`}
                          />
                        )}
                        <div
                          className="workflow-builder__node-content"
                          ref={(element) => registerNodeElement(block.id, element)}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedBlockId(block.id);
                          }}
                        >
                          <header className="workflow-builder__node-header">
                            <span className="workflow-builder__node-type">{definition.title}</span>
                          </header>
                          <div className="workflow-builder__node-body">
                            <input
                              type="text"
                              value={block.name}
                              onChange={(event) => handleNodeNameChange(block.id, event.target.value)}
                              aria-label={`${definition.title} name`}
                            />
                            {hasCode && (
                              <pre aria-label={`${definition.title} code sample`}>
                                {(block.code ?? '').slice(0, 120) || 'No code defined yet.'}
                              </pre>
                            )}
                            <div className="workflow-builder__node-io">
                              <div className="workflow-builder__node-io-group">
                                <span className="workflow-builder__node-io-label">Inputs</span>
                                <div className="workflow-builder__node-io-chips">
                                  {block.inputs.length === 0 ? (
                                    <span className="workflow-builder__node-chip workflow-builder__node-chip--empty">None</span>
                                  ) : (
                                    block.inputs.map((input) => (
                                      <span key={input} className="workflow-builder__node-chip">
                                        {input}
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                              <div className="workflow-builder__node-io-group">
                                <span className="workflow-builder__node-io-label">Outputs</span>
                                <div className="workflow-builder__node-io-chips">
                                  {block.outputs.length === 0 ? (
                                    <span className="workflow-builder__node-chip workflow-builder__node-chip--empty">None</span>
                                  ) : (
                                    block.outputs.map((output) => (
                                      <span key={output} className="workflow-builder__node-chip workflow-builder__node-chip--output">
                                        {output}
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {allowsOutgoingConnections &&
                          (block.type === 'if'
                            ? (block.outputs.length > 0 ? block.outputs : ['']).map((outputName, index) => {
                                const ratio = getOutputHandleRatioByIndex(block, index);
                                const handleLabel = typeof outputName === 'string' ? outputName : '';
                                return (
                                  <button
                                    key={`${block.id}-output-handle-${handleLabel || index}`}
                                    type="button"
                                    className="workflow-builder__connector workflow-builder__connector--output"
                                    style={{ top: `${ratio * 100}%` }}
                                    onPointerDown={(event) => startConnectionDrag(block.id, event, handleLabel)}
                                    aria-label={
                                      handleLabel
                                        ? `Start connection from ${formatBlockLabel(block)} (${handleLabel})`
                                        : `Start connection from ${formatBlockLabel(block)}`
                                    }
                                  />
                                );
                              })
                            : (
                                <button
                                  type="button"
                                  className="workflow-builder__connector workflow-builder__connector--output"
                                  onPointerDown={(event) => startConnectionDrag(block.id, event)}
                                  aria-label={`Start connection from ${formatBlockLabel(block)}`}
                                />
                              ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {(selectedBlock || selectedConnectionDetails) ? (
                <aside
                  className={`workflow-builder__inspector${
                    isCanvasFullscreen ? ' workflow-builder__inspector--floating' : ''
                  }`}
                >
                  <div className="workflow-builder__inspector-header">
                    <h3>Inspector</h3>
                    <p className="workflow-builder__inspector-supporting">
                      {selectedBlock
                        ? 'Adjust properties, IO, and code for this block.'
                        : 'Manage or remove the selected connection.'}
                    </p>
                  </div>
                  {selectedBlock && (
                    <div className="workflow-builder__inspector-card">
                      <div className="workflow-builder__summary">
                        <span className={`workflow-builder__tag workflow-builder__tag--${selectedBlock.type}`}>
                          {selectedDefinition?.title ?? BLOCK_TYPE_TITLES[selectedBlock.type]}
                        </span>
                        <span className="workflow-builder__summary-position">
                          ({Math.round(selectedBlock.position.x)}, {Math.round(selectedBlock.position.y)})
                        </span>
                      </div>
                      {selectedDefinition?.description && (
                        <p className="workflow-builder__details-hint">{selectedDefinition.description}</p>
                      )}
                      <div className="workflow-builder__form-group">
                        <label htmlFor="block-name">Block name</label>
                        <input id="block-name" type="text" value={selectedBlock.name} onChange={handleBlockNameChange} />
                      </div>
                      {canEditInputs && (
                        <div className="workflow-builder__form-group">
                          <div className="workflow-builder__io-editor-header">
                            <label htmlFor="block-inputs">
                              {selectedBlock.type === 'start' ? 'Expected inputs' : 'Inputs'}
                            </label>
                            <button type="button" className="secondary-button" onClick={() => handleAddPort('inputs')}>
                              Add input
                            </button>
                          </div>
                          {selectedBlock.type === 'start' ? (
                            <p className="workflow-builder__details-hint">
                              Define the JSON fields API callers should supply when executing this workflow.
                            </p>
                          ) : null}
                          {selectedBlock.inputs.length === 0 && (
                            <p className="workflow-builder__details-hint">
                              {selectedBlock.type === 'start' ? 'No required inputs.' : 'No inputs defined yet.'}
                            </p>
                          )}
                          <div className="workflow-builder__io-editor-list" id="block-inputs">
                            {selectedBlock.inputs.map((input, index) => (
                              <div key={`${selectedBlock.id}-input-${index}`} className="workflow-builder__io-editor-row">
                                <input
                                  type="text"
                                  value={input}
                                  placeholder="e.g. payload.customerId"
                                  onChange={(event) => handlePortChange('inputs', index, event.target.value)}
                                />
                                <button
                                  type="button"
                                  className="ghost-button"
                                  onClick={() => handleRemovePort('inputs', index)}
                                  aria-label={`Remove input ${input || index + 1}`}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {canEditOutputs && (
                        <div className="workflow-builder__form-group">
                          <div className="workflow-builder__io-editor-header">
                            <label htmlFor="block-outputs">
                              {selectedBlock.type === 'transform'
                                ? 'Transform outputs'
                                : selectedBlock.type === 'end'
                                  ? 'Expected outputs'
                                  : 'Outputs'}
                            </label>
                            <button type="button" className="secondary-button" onClick={() => handleAddPort('outputs')}>
                              Add output
                            </button>
                          </div>
                          {selectedBlock.type === 'transform' ? (
                            <>
                              {selectedTransformMappings.length === 0 && (
                                <p className="workflow-builder__details-hint">
                                  No outputs defined yet. Add a mapping to expose structured fields.
                                </p>
                              )}
                              <div className="workflow-builder__transform-list" id="block-outputs">
                                {selectedTransformMappings.map((mapping, index) => (
                                  <div
                                    key={`${selectedBlock.id}-transform-${index}`}
                                    className="workflow-builder__transform-row"
                                  >
                                    <div className="workflow-builder__transform-field">
                                      <span>Source path</span>
                                      <input
                                        type="text"
                                        value={mapping.source}
                                        placeholder="e.g. context.payload.sub1"
                                        onChange={(event) => handleTransformSourceChange(index, event.target.value)}
                                      />
                                    </div>
                                    <div className="workflow-builder__transform-field">
                                      <span>Output name</span>
                                      <input
                                        type="text"
                                        value={selectedBlock.outputs[index] ?? mapping.target}
                                        placeholder="e.g. payload.sub1"
                                        onChange={(event) => handlePortChange('outputs', index, event.target.value)}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="ghost-button"
                                      onClick={() => handleRemovePort('outputs', index)}
                                      aria-label={`Remove transform output ${mapping.target || index + 1}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : selectedBlock.type === 'if' ? (
                            <>
                              {selectedConditionRules.length === 0 && (
                                <p className="workflow-builder__details-hint">
                                  Add branches to route data down different paths.
                                </p>
                              )}
                              <div className="workflow-builder__condition-list" id="block-outputs">
                                {selectedConditionRules.map((rule, index) => (
                                  <div key={`${selectedBlock.id}-condition-${rule.id}`} className="workflow-builder__condition-row">
                                    <div className="workflow-builder__condition-field">
                                      <label htmlFor={`condition-output-${rule.id}`}>Branch label</label>
                                      <input
                                        id={`condition-output-${rule.id}`}
                                        type="text"
                                        value={selectedBlock.outputs[index] ?? rule.output}
                                        onChange={(event) => handlePortChange('outputs', index, event.target.value)}
                                      />
                                    </div>
                                    <div className="workflow-builder__condition-field">
                                      <label htmlFor={`condition-operator-${rule.id}`}>Operator</label>
                                      <select
                                        id={`condition-operator-${rule.id}`}
                                        value={rule.operator}
                                        onChange={(event) =>
                                          handleConditionOperatorChange(index, event.target.value as ConditionOperator)
                                        }
                                      >
                                        <option value="equals">equals</option>
                                        <option value="contains">contains</option>
                                        <option value="isEmpty">is empty</option>
                                        <option value="always">else</option>
                                      </select>
                                    </div>
                                    {rule.operator !== 'always' && (
                                      <div className="workflow-builder__condition-field">
                                        <label htmlFor={`condition-left-${rule.id}`}>Input path</label>
                                        <input
                                          id={`condition-left-${rule.id}`}
                                          type="text"
                                          value={rule.left}
                                          onChange={(event) => handleConditionLeftChange(index, event.target.value)}
                                          placeholder="e.g. payload.status"
                                        />
                                      </div>
                                    )}
                                    {(rule.operator === 'equals' || rule.operator === 'contains') && (
                                      <div className="workflow-builder__condition-field">
                                        <label htmlFor={`condition-right-${rule.id}`}>Compare value</label>
                                        <input
                                          id={`condition-right-${rule.id}`}
                                          type="text"
                                          value={rule.right ?? ''}
                                          onChange={(event) => handleConditionRightChange(index, event.target.value)}
                                          placeholder="e.g. success"
                                        />
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="ghost-button workflow-builder__condition-remove"
                                      onClick={() => handleRemoveConditionRule(index)}
                                      disabled={selectedConditionRules.length <= 1}
                                      aria-label={`Remove branch ${rule.output || index + 1}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button type="button" className="secondary-button" onClick={handleAddConditionRule}>
                                Add branch
                              </button>
                            </>
                          ) : selectedBlock.type === 'end' ? (
                            <>
                              {selectedBlock.outputs.length === 0 && (
                                <p className="workflow-builder__details-hint">
                                  List the fields this workflow will return to API clients.
                                </p>
                              )}
                              <div className="workflow-builder__io-editor-list" id="block-outputs">
                                {selectedBlock.outputs.map((output, index) => (
                                  <div key={`${selectedBlock.id}-output-${index}`} className="workflow-builder__io-editor-row">
                                    <input
                                      type="text"
                                      value={output}
                                      placeholder="e.g. summary"
                                      onChange={(event) => handlePortChange('outputs', index, event.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="ghost-button"
                                      onClick={() => handleRemovePort('outputs', index)}
                                      aria-label={`Remove output ${output || index + 1}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <>
                              {selectedBlock.outputs.length === 0 && (
                                <p className="workflow-builder__details-hint">No outputs defined yet.</p>
                              )}
                              <div className="workflow-builder__io-editor-list" id="block-outputs">
                                {selectedBlock.outputs.map((output, index) => (
                                  <div key={`${selectedBlock.id}-output-${index}`} className="workflow-builder__io-editor-row">
                                    <input
                                      type="text"
                                      value={output}
                                      placeholder="e.g. json.sub1"
                                      onChange={(event) => handlePortChange('outputs', index, event.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="ghost-button"
                                      onClick={() => handleRemovePort('outputs', index)}
                                      aria-label={`Remove output ${output || index + 1}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {selectedBlock.type === 'llm' && selectedLlmConfig && (
                        <>
                          <div className="workflow-builder__form-group">
                            <label htmlFor="llm-endpoint">Endpoint URL</label>
                            <input
                              id="llm-endpoint"
                              type="text"
                              value={selectedLlmConfig.endpoint}
                              onChange={(event) => handleLlmConfigChange('endpoint', event.target.value)}
                              placeholder="https://example.com/llm"
                            />
                          </div>
                          <div className="workflow-builder__form-group">
                            <label htmlFor="llm-deployment">Deployment name</label>
                            <input
                              id="llm-deployment"
                              type="text"
                              value={selectedLlmConfig.deploymentName}
                              onChange={(event) => handleLlmConfigChange('deploymentName', event.target.value)}
                              placeholder="e.g. gpt-4"
                            />
                          </div>
                          <div className="workflow-builder__form-group">
                            <label htmlFor="llm-api-key">API key</label>
                            <input
                              id="llm-api-key"
                              type="password"
                              value={selectedLlmConfig.apiKey}
                              onChange={(event) => handleLlmConfigChange('apiKey', event.target.value)}
                              placeholder="Enter the API key"
                            />
                          </div>
                          <div className="workflow-builder__form-group">
                            <label htmlFor="llm-prompt-source">Prompt source</label>
                            {llmPromptOptions.length === 0 ? (
                              <p className="workflow-builder__details-hint">Add an input or global variable to reference as the prompt.</p>
                            ) : (
                              (() => {
                                const selectionKey = `${selectedLlmConfig.promptSource.type}:${selectedLlmConfig.promptSource.value}`;
                                const hasSelection = llmPromptOptions.some(
                                  (option) => `${option.type}:${option.value}` === selectionKey
                                );
                                const currentValue = hasSelection ? selectionKey : '';
                                return (
                                  <select
                                    id="llm-prompt-source"
                                    value={currentValue}
                                    onChange={(event) => {
                                      const [scope, sourceValue = ''] = event.target.value.split(':', 2);
                                      handleLlmPromptSourceChange(scope === 'global' ? 'global' : 'input', sourceValue);
                                    }}
                                  >
                                    <option value="">Select a source</option>
                                    {llmPromptOptions.map((option) => (
                                      <option key={`${option.type}-${option.value}`} value={`${option.type}:${option.value}`}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                );
                              })()
                            )}
                          </div>
                        </>
                      )}
                      {selectedDefinition?.capabilities?.code && (
                        <div className="workflow-builder__form-group">
                          <label htmlFor="block-code">Code</label>
                          <textarea
                            id="block-code"
                            rows={6}
                            value={selectedBlock.code ?? ''}
                            onChange={handleBlockCodeChange}
                            placeholder="Add inline code or prompt instructions for this block."
                          />
                        </div>
                      )}
                      {selectedDefinition?.capabilities?.notes && (
                        <p className="workflow-builder__details-hint">{selectedDefinition.capabilities.notes}</p>
                      )}
                      {selectedBlock.type !== 'start' && (
                        <button type="button" className="danger-button" onClick={handleDeleteBlock}>
                          Remove block
                        </button>
                      )}
                    </div>
                  )}
                  {!selectedBlock && selectedConnectionDetails && (
                    <div className="workflow-builder__inspector-card">
                      <div className="workflow-builder__summary workflow-builder__summary--connection">
                        <span>{formatBlockLabel(selectedConnectionDetails.from)}</span>
                        {selectedConnectionDetails.branchLabel && (
                          <span className="workflow-builder__connection-branch">{selectedConnectionDetails.branchLabel}</span>
                        )}
                        <span aria-hidden="true">→</span>
                        <span>{formatBlockLabel(selectedConnectionDetails.to)}</span>
                      </div>
                      <p className="workflow-builder__details-hint">
                        {selectedConnectionDetails.branchLabel
                          ? `Data flows when the "${selectedConnectionDetails.branchLabel}" branch is selected.`
                          : 'Connections move data from an output on the first block to an input on the second block.'}
                      </p>
                      <button
                        type="button"
                        className="danger-button"
                        onClick={() => {
                          handleRemoveConnection(selectedConnectionDetails.connection);
                          setSelectedConnection(null);
                        }}
                      >
                        Remove connection
                      </button>
                    </div>
                  )}
                </aside>
              ) : (
                <aside
                  className={`workflow-builder__inspector${
                    isCanvasFullscreen ? ' workflow-builder__inspector--floating' : ''
                  }`}
                >
                  <div className="workflow-builder__inspector-header">
                    <h3>Inspector</h3>
                    <p>Select a block or connection to edit its details.</p>
                  </div>
                  <p className="workflow-builder__details-empty">Select a block or connection to see its configuration.</p>
                </aside>
              )}
            </div>
            {isCanvasFullscreen && isFullscreenPaletteOpen && (
              <div className="workflow-builder__floating-palette">
                <div className="workflow-builder__floating-palette-header">
                  <strong>Block palette</strong>
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => setIsFullscreenPaletteOpen(false)}
                    aria-label="Close block palette"
                  >
                    Close
                  </button>
                </div>
                <div className="workflow-builder__floating-palette-items">
                  {BLOCK_TYPES.map((type) => {
                    const definition = getBlockDefinition(type);
                    return (
                      <button
                        key={`floating-${type}`}
                        type="button"
                        className={`workflow-builder__palette-item workflow-builder__palette-item--${type}`}
                        onClick={() => {
                          setIsFullscreenPaletteOpen(false);
                          handleAddBlockClick(type);
                        }}
                        draggable
                        onDragStart={(event) => handlePaletteDragStart(event, type)}
                        title={definition.description}
                      >
                        {definition.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

export default WorkflowBuilderFeature;
